import { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { APP_NAME } from '@/lib/brand';
import { useAuth } from '@/contexts/AuthContext';
import { isCarDealerUser, getCompanyVertical, isCatalogVerticalUser } from '@/lib/companyVertical';
import api from '@/lib/api';
import { usePlatformBaseUrl } from '@/lib/platformBaseUrl';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PhoneInput from '@/components/PhoneInput';
import CorreoConfig from './settings/CorreoConfig';
import WhatsAppPage from '@/pages/WhatsAppPage';
import FaqPropuestasPanel from './settings/FaqPropuestasPanel';
import FaqMediaFields from '@/components/FaqMediaFields';
import HelpTip from '@/components/HelpTip';
import PromptAccionesEditor from '@/components/PromptAccionesEditor';
import { Settings, Palette, Phone, PhoneCall, Plus, X, Save, Key, MessageSquare, Copy, Trash2, CheckCircle2, Globe, Bot, Zap, BarChart3, Activity, Wifi, WifiOff, QrCode, Loader2, Power, PowerOff, UserCheck, Info, ExternalLink, Shield, Eye, EyeOff, Calendar, Link, FileText, FlaskConical, Download, Pencil, RefreshCw, Users, Mail, Send, Video, CreditCard, DollarSign, MessageCircle, Layers, Package, BrainCircuit, Database, ShieldAlert, Image as ImageIcon, Sparkles } from 'lucide-react';

// WS-IA-UNIFIED F3 — iconos por skill key (catálogo /api/ai/skills-catalog).
const SKILL_ICONS = {
  enviar_ficha: FileText,
  enviar_brochure: FileText,
  enviar_imagenes: ImageIcon,
  enviar_video: Video,
  enviar_url: Link,
  enviar_media_faq: Layers,
  enviar_plano: FileText,
  iniciar_llamada: PhoneCall,
  programar_llamada: Phone,
  agendar_cita: Calendar,
  proponer_cita: Calendar,
  cambiar_intent: RefreshCw,
  capturar_email: Mail,
  capturar_nombre: UserCheck,
  no_contactar: ShieldAlert,
};
import { Switch } from '@/components/ui/switch';
import { QRCodeSVG } from 'qrcode.react';
import AICampaignIndependentPage from '@/pages/AICampaignIndependentPage';

export default function SettingsPage() {
  const { user, refreshUser, impersonating } = useAuth();
  const _wabaOn = (user?.allowed_modules || user?.company?.allowed_modules || {}).waba !== false;
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'superadmin';
  const allowedModules = user?.allowed_modules || user?.license_profile?.included_modules || {};
  const licenseType = String(user?.license_profile?.license_type || user?.business_type || 'agency').toLowerCase();
  const isDeveloper = licenseType === 'developer';
  // E-R3c: detección de vertical centralizada en lib/companyVertical.js.
  const isCarDealer = isCarDealerUser(user);
  const isCatalog = isCatalogVerticalUser(user);
  // tipo-operacion-renta-venta: gate por VERTICAL (user.dashboard_vertical), NUNCA por
  // allowedModules/isDeveloper (señales de módulo/licencia, no de vertical -- mismo
  // criterio que LeadsPage.js).
  const showOperationType = user?.dashboard_vertical === 'developments' || user?.dashboard_vertical === 'real_estate';
  const companyNameLabel = {
    independent: 'Tu nombre profesional',
    developer: 'Nombre del Desarrollador',
    franchise: 'Nombre de la Franquicia',
    owner: 'Nombre del Propietario',
    car_dealer: 'Nombre de la Agencia Automotriz',
    'car-dealer': 'Nombre de la Agencia Automotriz',
    agency: 'Nombre de la Agencia',
  }[licenseType] || 'Nombre de la Empresa';
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Estado de API Keys removido — ahora vive en /api-hub (ApiHubPage)
  const [aiModels, setAiModels] = useState([]);
  const [waStatus, setWaStatus] = useState(null);
  const [waConnecting, setWaConnecting] = useState(false);
  const [waQr, setWaQr] = useState(null);
  const [agentConfig, setAgentConfig] = useState({});
  const [skillsCatalog, setSkillsCatalog] = useState([]);
  const [agentSaving, setAgentSaving] = useState(false);
  const [planCaps, setPlanCaps] = useState({});
  useEffect(() => { api.get('/me/plan').then(r => setPlanCaps(r.data?.capabilities || {})).catch(() => {}); }, []);
  const [cloudConfig, setCloudConfig] = useState(null);
  const [cloudSaving, setCloudSaving] = useState(false);
  const [cloudForm, setCloudForm] = useState({ phone_number_id: '', access_token: '', waba_id: '', app_id: '', app_secret: '' });
  const [showTokens, setShowTokens] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplForm, setTplForm] = useState({ name: '', category: 'MARKETING', language: 'es', body_text: '', header_text: '', footer_text: '', header_type: 'TEXT', header_media_url: '' });
  const [tplCreating, setTplCreating] = useState(false);
  const [showTplForm, setShowTplForm] = useState(false);
  const [abVariants, setAbVariants] = useState([]);
  const [abForm, setAbForm] = useState({ name: '', headline: '', description: '', cta_text: '' });
  const [abCreating, setAbCreating] = useState(false);
  const [commConfig, setCommConfig] = useState(null);
  const [commSaving, setCommSaving] = useState(false);
  const [wlConfig, setWlConfig] = useState({ custom_domain: '' });
  const [wlSaving, setWlSaving] = useState(false);
  const [aiApiKeys, setAiApiKeys] = useState({ openai: '', anthropic: '', google: '' });
  // El backend ya NO devuelve las llaves (eran legibles por cualquier usuario desde
  // el Network). Solo manda que proveedores estan configurados; el input queda
  // vacio y escribir uno nuevo lo reemplaza. Ver core/company_sanitize.py.
  const [aiKeysConfigured, setAiKeysConfigured] = useState({ openai: false, anthropic: false, google: false });
  const keyPlaceholder = (proveedor, ejemplo) =>
    aiKeysConfigured?.[proveedor] ? '•••••••••••• (configurada — escribe una nueva para reemplazarla)' : ejemplo;
  const [aiKeysSaving, setAiKeysSaving] = useState(false);
  const [dnsProvider, setDnsProvider] = useState('general');
  // Host oficial de la plataforma (dominio servido por el backend, sin esquema) para las instrucciones CNAME.
  const platformHost = (usePlatformBaseUrl() || '').replace(/^https?:\/\//, '');
  const [campaigns, setCampaigns] = useState([]);
  // Fix 6.3: presupuesto mensual por campana -- {campaign_id: {amount, period, locked}}
  const [campaignBudgets, setCampaignBudgets] = useState({});
  const [budgetDrafts, setBudgetDrafts] = useState({});
  const [budgetSaving, setBudgetSaving] = useState({});
  const currentPeriod = new Date().toISOString().slice(0, 7);

  const loadCampaignBudgets = () => {
    api.get('/dashboard/v2/campaign-budget').then(r => {
      const map = {};
      (r.data?.campaigns || []).forEach(c => { map[c.campaign_id] = c.budget; });
      setCampaignBudgets(map);
    }).catch(() => {});
  };
  useEffect(() => { loadCampaignBudgets(); }, []);

  const saveCampaignBudget = async (campaignId) => {
    const raw = budgetDrafts[campaignId];
    const amount = parseFloat(raw);
    if (Number.isNaN(amount) || amount < 0) { toast.error('Monto inválido'); return; }
    setBudgetSaving(s => ({ ...s, [campaignId]: true }));
    try {
      await api.put(`/campaigns/${campaignId}/budgets/${currentPeriod}`, { amount, currency: 'MXN' });
      toast.success('Presupuesto guardado');
      loadCampaignBudgets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setBudgetSaving(s => ({ ...s, [campaignId]: false }));
    }
  };
  const [campaignForm, setCampaignForm] = useState({ name: '', medium: 'digital', channel: '', channel_manual: '', call_mode: '', link_type: 'form', whatsapp_message: '', whatsapp_number: '', form_url: '', inventory_intent: '', development_id: '', operation_type: '' });
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [campaignGroups, setCampaignGroups] = useState([]);
  const [campaignDevelopments, setCampaignDevelopments] = useState([]);
  const [campaignProducts, setCampaignProducts] = useState([]);
  const [campaignCarModels, setCampaignCarModels] = useState([]);
  const [aiIndependentCampaigns, setAiIndependentCampaigns] = useState([]);
  const [validatingSite, setValidatingSite] = useState(false);
  const [campaignChannels, setCampaignChannels] = useState({ digital: [], print: [], llamada: [] });
  const [landingTemplates, setLandingTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [templateSaving, setTemplateSaving] = useState(false);
  const [notifConfig, setNotifConfig] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('general');
  const [iaSub, setIaSub] = useState('cerebro');
  const [rouletteDedup, setRouletteDedup] = useState({
    dedup_scope: 'company',
    dedup_window_days: 0,
    dedup_on_duplicate: 'keep_owner',
  });
  const [rouletteDedupSaving, setRouletteDedupSaving] = useState(false);
  const loadSettings = () => {
    Promise.all([api.get('/settings'), api.get('/ai/models'), api.get('/whatsapp/status').catch(() => ({ data: null })), api.get('/ai-agent/config').catch(() => ({ data: null })), api.get('/whatsapp/cloud-config').catch(() => ({ data: null })), api.get('/team').catch(() => ({ data: [] })), api.get('/ab-tests').catch(() => ({ data: { variants: [] } })), api.get('/commissions/config').catch(() => ({ data: null })), api.get('/whitelabel').catch(() => ({ data: { custom_domain: '' } })), api.get('/campaigns', { params: { include_hidden: true } }).catch(() => ({ data: [] })), api.get('/campaigns/channels').catch(() => ({ data: { digital: [], print: [] } })), api.get('/rules').catch(() => ({ data: [] })), api.get('/pipeline').catch(() => ({ data: [] }))])
      .then(([s, m, w, ag, cc, tm, ab, cm, wl, camps, cch, rl, ps]) => { setSettings(s.data); setAiModels(m.data); setWaStatus(w.data); setAgentConfig(ag.data || {}); setCloudConfig(cc.data); if(cc.data?.configured){setCloudForm(f=>({...f,phone_number_id:cc.data.phone_number_id||'',waba_id:cc.data.waba_id||'',app_id:cc.data.app_id||''}))} setTeamMembers(Array.isArray(tm.data) ? tm.data : []); setAbVariants(ab.data?.variants || []); setCommConfig(cm.data); setWlConfig(wl.data || { custom_domain: '' }); if(s.data?.ai_api_keys_configured){setAiKeysConfigured(s.data.ai_api_keys_configured)} setCampaigns(Array.isArray(camps.data) ? camps.data : []); setCampaignChannels(cch.data || {digital:[],print:[]});
        Promise.all([api.get('/landing-templates'), api.get('/landing-templates/current').catch(() => ({data:{template_id:'classic'}}))]).then(([tpls, cur]) => { setLandingTemplates(tpls.data || []); setSelectedTemplate(cur.data?.template_id || 'classic'); });
        api.get('/roulette/config').catch(() => ({ data: {} })).then(rcRes => { const rc = rcRes.data || {}; setRouletteDedup({ dedup_scope: rc.dedup_scope || 'company', dedup_window_days: rc.dedup_window_days ?? 0, dedup_on_duplicate: rc.dedup_on_duplicate || 'keep_owner' }); });
        setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadSettings(); }, []);

  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get('tab') === 'whatsapp') {
        navigate('/whatsapp', { replace: true });
        return;
      }
      if (q.get('tab') === 'reglas') {
        navigate('/opportunities?seccion=reglas', { replace: true });
        return;
      }
      const _t = q.get('tab');
      if (['general','ia','whatsapp','integraciones','comisiones','whitelabel','templates','campanas','marcas'].includes(_t)) setActiveMainTab(_t);
      if (q.get('sub') === 'campanas') {
        setActiveMainTab('ia');
        setIaSub('campanas');
      }
    } catch (e) { /* ignore */ }
  }, []);

  // WS-IA-UNIFIED F3 — catálogo de habilidades (skills) filtrado por vertical.
  useEffect(() => {
    let active = true;
    (async () => {
      const { industry, businessType } = getCompanyVertical(user);
      const vertical = industry || businessType || '';
      try {
        const url = vertical ? `/ai/skills-catalog?vertical=${vertical}` : '/ai/skills-catalog';
        const r = await api.get(url);
        if (active) setSkillsCatalog(r.data?.skills || []);
      } catch {
        try {
          const r2 = await api.get('/ai/skills-catalog');
          if (active) setSkillsCatalog(r2.data?.skills || []);
        } catch {
          if (active) setSkillsCatalog([]);
        }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAgentSkill = (skillKey) => setAgentConfig(prev => {
    const current = (prev && prev.active_skills) || [];
    const next = current.includes(skillKey) ? current.filter(k => k !== skillKey) : [...current, skillKey];
    return { ...prev, active_skills: next };
  });

  const updateKnowledgeFaqItem = (idx, next) => setAgentConfig((prev) => {
    const items = [...((prev && prev.knowledge_faq_items) || [])];
    items[idx] = next;
    return { ...prev, knowledge_faq_items: items };
  });
  const addKnowledgeFaqItem = () => setAgentConfig((prev) => ({
    ...prev,
    knowledge_faq_items: [...((prev && prev.knowledge_faq_items) || []), { question: '', answer: '', images: [], pdfs: [], videos: [] }],
  }));
  const removeKnowledgeFaqItem = (idx) => setAgentConfig((prev) => ({
    ...prev,
    knowledge_faq_items: ((prev && prev.knowledge_faq_items) || []).filter((_, i) => i !== idx),
  }));

  // Cargar grupos de asignación cuando el modal de campaña está abierto.
  // E-R4: ahora el selector aparece para TODOS los link_types, no solo whatsapp.
  useEffect(() => {
    if (campaignDialog && campaignGroups.length === 0) {
      api.get('/roulette/groups').then(r => {
        const all = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setCampaignGroups(all.filter(g => g.is_active !== false));
      }).catch(() => setCampaignGroups([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDialog]);

  // Vertical desarrollos: cargar la lista de desarrollos para el selector de campaña.
  useEffect(() => {
    if (campaignDialog && isDeveloper && campaignDevelopments.length === 0) {
      api.get('/developments').then(r => {
        const all = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setCampaignDevelopments(all);
      }).catch(() => setCampaignDevelopments([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDialog, isDeveloper]);

  // Consolidación: cargar productos/modelos para el selector de ALCANCE (antes vivía
  // duplicado en el panel dentro de Productos/Modelos). Por datos: solo la lista de SU vertical.
  useEffect(() => {
    if (!campaignDialog || isDeveloper) return;
    if (allowedModules.products && campaignProducts.length === 0) {
      api.get('/products').then(r => {
        const all = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.products || []);
        setCampaignProducts(all);
      }).catch(() => {});
    }
    if (!allowedModules.products && campaignCarModels.length === 0) {
      api.get('/car-models').then(r => {
        const all = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.models || []);
        setCampaignCarModels(all);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDialog, isDeveloper]);

  // Cargar campañas IA independientes cuando el modal está abierto y el
  // link_type es WhatsApp (para el selector de agente IA por campaña).
  useEffect(() => {
    if (campaignDialog && campaignForm.link_type === 'whatsapp' && aiIndependentCampaigns.length === 0) {
      api.get('/ai-independent-campaigns').then(r => {
        const all = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setAiIndependentCampaigns(all);
      }).catch(() => setAiIndependentCampaigns([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignDialog, campaignForm.link_type]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const data = {
        name: settings.name || '', logo_url: settings.logo_url || '',
        primary_color: settings.primary_color || '#A5D62C', secondary_color: settings.secondary_color || '#1B5E7B',
        tertiary_color: settings.tertiary_color || '#0B0C10',
        contact_email: settings.contact_email || '', contact_phone: settings.contact_phone || '',
        address: settings.address || '',
        website: settings.website || '', description: settings.description || '',
        timezone: settings.timezone || 'America/Mexico_City',
        business_hours: settings.business_hours || { enabled: false, days: [0,1,2,3,4], start: '09:00', end: '18:00' },
        ai_model: settings.ai_model || 'openai/gpt-4.1-mini',
        ai_temperature: settings.ai_temperature ?? 0.4,
        landing_enabled: settings.landing_enabled || false,
        slug: settings.slug || '',
        landing_headline: settings.landing_headline || '',
        landing_description: settings.landing_description || '',
        duplicate_detection_enabled: settings.duplicate_detection_enabled || false,
      };
      const res = await api.put('/settings', data);
      setSettings(res.data);
      setSaved(true);
      toast.success('Configuracion guardada correctamente');
      if (refreshUser) refreshUser();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const saveRouletteDedup = async () => {
    setRouletteDedupSaving(true);
    try {
      await api.put('/roulette/config', rouletteDedup);
      toast.success('Configuracion de duplicidad guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setRouletteDedupSaving(false);
    }
  };

  // API Keys: gestión migrada a /api-hub (ApiHubPage > ApiKeysManager).

  const downloadQr = () => {
    const svg = document.getElementById('landing-qr-svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const a = document.createElement('a');
      a.download = `qr-${settings?.slug || 'landing'}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const [agentAnalyzeDialog, setAgentAnalyzeDialog] = useState(false);
  const [agentAnalyzeResult, setAgentAnalyzeResult] = useState(null);
  const [agentAnalyzePending, setAgentAnalyzePending] = useState(null);
  const [agentAnalyzing, setAgentAnalyzing] = useState(false);

  const _persistAgentConfig = async (configToSave) => {
    setAgentSaving(true);
    try {
      const r = await api.put('/ai-agent/config', configToSave);
      setAgentConfig(r.data || {});
      toast.success('Configuracion del agente actualizada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setAgentSaving(false); }
  };
// Car-dealer: análisis IA de inventario hardcodeado antes de guardar

  const saveAgentConfig = async (updates) => {
    const newConfig = { ...agentConfig, ...updates };
    const promptText = String(newConfig.system_prompt_override || '').trim();
    const faqsText = String(newConfig.knowledge_faqs || '').trim();

    // Solo car-dealer dispara análisis IA y solo si hay contenido a analizar.
    if (!isCarDealer || (!promptText && !faqsText)) {
      return _persistAgentConfig(newConfig);
    }

    setAgentAnalyzing(true);
    try {
      const r = await api.post('/ai-agent/analyze-config-content', {
        prompt_text: promptText,
        faqs_text: faqsText,
      });
      const analysis = r.data || {};
      if (analysis.contains_inventory) {
        setAgentAnalyzeResult(analysis);
        setAgentAnalyzePending(newConfig);
        setAgentAnalyzeDialog(true);
        return; // Esperamos decisión del usuario en el modal
      }
    } catch (e) {
      // Backend devuelve fallback en errores; sólo cae aquí si la red murió.
      // Nunca bloquear al usuario.
    } finally {
      setAgentAnalyzing(false);
    }
    await _persistAgentConfig(newConfig);
  };

  const switchWaMode = async (mode) => {
    try {
      await api.post('/whatsapp/set-mode', { mode });
      const [w, cc] = await Promise.all([api.get('/whatsapp/status'), api.get('/whatsapp/cloud-config').catch(() => ({ data: null }))]);
      setWaStatus(w.data); setCloudConfig(cc.data);
      if (cc.data?.configured) setCloudForm(f => ({ ...f, phone_number_id: cc.data.phone_number_id || '', waba_id: cc.data.waba_id || '', app_id: cc.data.app_id || '' }));
      toast.success(mode === 'cloud' ? 'Modo Cloud API activado' : 'Modo QR activado');
    } catch (e) { toast.error('Error al cambiar modo'); }
  };

  const saveCloudConfig = async () => {
    if (!cloudForm.phone_number_id || !cloudForm.access_token || !cloudForm.waba_id) {
      toast.error('Phone Number ID, Access Token y WABA ID son requeridos');
      return;
    }
    setCloudSaving(true);
    try {
      const r = await api.post('/whatsapp/cloud-config', cloudForm);
      setCloudConfig(r.data);
      const s = await api.get('/whatsapp/status');
      setWaStatus(s.data);
      toast.success('Configuracion Cloud API guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setCloudSaving(false); }
  };

  const loadTemplates = async () => {
    setTplLoading(true);
    try {
      const r = await api.get('/whatsapp/templates');
      setWaTemplates(r.data?.templates || []);
    } catch { }
    finally { setTplLoading(false); }
  };

  const createTemplate = async () => {
    if (!tplForm.name || !tplForm.body_text) { toast.error('Nombre y cuerpo son requeridos'); return; }
    setTplCreating(true);
    try {
      await api.post('/whatsapp/templates', tplForm);
      toast.success('Template enviado a Meta para aprobacion');
      setTplForm({ name: '', category: 'MARKETING', language: 'es', body_text: '', header_text: '', footer_text: '', header_type: 'TEXT', header_media_url: '' });
      setShowTplForm(false);
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear template'); }
    finally { setTplCreating(false); }
  };

  const deleteTemplate = async (name) => {
    try {
      await api.delete(`/whatsapp/templates/${name}`);
      setWaTemplates(t => t.filter(x => x.name !== name));
      toast.success('Template eliminado');
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const createAbVariant = async () => {
    if (!abForm.name || !abForm.headline) { toast.error('Nombre y titulo son requeridos'); return; }
    setAbCreating(true);
    try {
      const r = await api.post('/ab-tests', abForm);
      setAbVariants(v => [...v, r.data]);
      setAbForm({ name: '', headline: '', description: '', cta_text: '' });
      toast.success('Variante creada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setAbCreating(false); }
  };

  const toggleAbVariant = async (id, active) => {
    try {
      const r = await api.put(`/ab-tests/${id}`, { is_active: active });
      setAbVariants(v => v.map(x => x.id === id ? r.data : x));
    } catch { toast.error('Error'); }
  };

  const deleteAbVariant = async (id) => {
    try {
      await api.delete(`/ab-tests/${id}`);
      setAbVariants(v => v.filter(x => x.id !== id));
      toast.success('Variante eliminada');
    } catch { toast.error('Error'); }
  };

  const saveCommissions = async () => {
    setCommSaving(true);
    try {
      const r = await api.put('/commissions/config', commConfig);
      setCommConfig(r.data);
      toast.success('Comisiones guardadas');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setCommSaving(false); }
  };

  const saveWhitelabel = async () => {
    setWlSaving(true);
    try {
      const r = await api.put('/whitelabel', wlConfig);
      setWlConfig(r.data);
      toast.success('Configuracion guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setWlSaving(false); }
  };

  const saveCampaign = async () => {
    const effectiveChannel = (campaignForm.channel_manual || '').trim() || campaignForm.channel;
    if (!campaignForm.name || !effectiveChannel) { toast.error('Nombre y canal requeridos'); return; }
    if (isDeveloper && !campaignForm.id && !campaignForm.development_id) { toast.error('Selecciona un desarrollo para la campaña'); return; }
    try {
      // Otras verticales no manejan desarrollo → no envían el campo.
      const payload = { ...campaignForm, channel: effectiveChannel };
      if (!isDeveloper) delete payload.development_id;
      if (campaignForm.id) {
        await api.put(`/campaigns/${campaignForm.id}`, payload);
      } else {
        await api.post('/campaigns', payload);
      }
      toast.success('Campana guardada');
      setCampaignDialog(false);
      loadSettings();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error al guardar campana';
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar campana');
    }
  };

  const deleteCampaign = async (id) => {
    // Fix (feedback owner jul2026): ya no se "elimina" -- se apaga (is_active=False,
    // backend rechaza reactivar). Confirm explicito porque es de un solo sentido.
    if (!window.confirm('Esta campaña se apagará y no se podrá volver a encender. Sus datos históricos se conservan. ¿Continuar?')) return;
    try { await api.delete(`/campaigns/${id}`); toast.success('Campaña apagada'); loadSettings(); } catch (e) { toast.error('Error'); }
  };

  const selectTemplate = async (tplId) => {
    setTemplateSaving(true);
    try {
      await api.put(`/landing-templates/${tplId}`);
      setSelectedTemplate(tplId);
      toast.success('Template actualizado');
    } catch (e) { toast.error('Error al seleccionar template'); }
    finally { setTemplateSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;


  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-white">Configuracion</h2>
        <p className="text-muted-foreground text-sm mt-1">Administra tu inmobiliaria, integraciones y asistente IA</p>
      </div>

      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} defaultValue="general" className="w-full">
        <TabsList className="bg-black/30 border border-white/5 mb-6 flex flex-wrap h-auto gap-1 p-1" data-testid="settings-tabs">
          <TabsTrigger value="general" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary text-xs gap-1.5" data-testid="tab-general">
            <Settings className="w-3.5 h-3.5" />General
          </TabsTrigger>
          <TabsTrigger value="ia" className="data-[state=active]:bg-[#A5D62C]/20 data-[state=active]:text-[#A5D62C] text-xs gap-1.5" data-testid="tab-ia">
            <Bot className="w-3.5 h-3.5" />Asistente IA
          </TabsTrigger>
          {_wabaOn && (
          <TabsTrigger value="whatsapp" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-xs gap-1.5" data-testid="tab-whatsapp">
            <MessageSquare className="w-3.5 h-3.5" />WhatsApp
          </TabsTrigger>
          )}
          <TabsTrigger value="integraciones" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-xs gap-1.5" data-testid="tab-integraciones">
            <Zap className="w-3.5 h-3.5" />Integraciones
          </TabsTrigger>
          <TabsTrigger value="comisiones" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs gap-1.5" data-testid="tab-comisiones">
            <BarChart3 className="w-3.5 h-3.5" />Comisiones
          </TabsTrigger>
          <TabsTrigger value="whitelabel" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-xs gap-1.5" data-testid="tab-whitelabel">
            <Globe className="w-3.5 h-3.5" />Marca Blanca
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-pink-500/20 data-[state=active]:text-pink-400 text-xs gap-1.5" data-testid="tab-templates">
            <FileText className="w-3.5 h-3.5" />Templates
          </TabsTrigger>
          <TabsTrigger value="campanas" className="data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-400 text-xs gap-1.5" data-testid="tab-campanas">
            <Zap className="w-3.5 h-3.5" />Campanas
          </TabsTrigger>
        </TabsList>

        {/* Aviso persistente: API Keys movidas al API Hub */}
        <div className="mb-5 p-3 rounded-lg bg-violet-500/5 border border-violet-400/20 flex items-center gap-3" data-testid="api-moved-banner">
          <Key className="w-4 h-4 text-violet-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white">
              <span className="font-semibold text-violet-200">¿Buscas API Keys?</span>
              <span className="text-white/60"> Ahora viven en el nuevo <strong>API Hub</strong> junto con el Generador de APIs y el Constructor de UTMs.</span>
            </p>
          </div>
          <Button asChild size="sm" className="bg-violet-500 hover:bg-violet-500/90 text-white text-xs h-7 shrink-0" data-testid="goto-api-hub-btn">
            <a href="/dev-hub?tab=keys">Ir al API Hub</a>
          </Button>
        </div>

        {/* ==================== TAB: GENERAL ==================== */}
        <TabsContent value="general" className="space-y-6">
          <Card className="bg-card/50 backdrop-blur-md border-white/5">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Settings className="w-4 h-4 text-primary" />Informacion General</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Datos principales de tu empresa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{companyNameLabel}</Label>
                <Input data-testid="settings-name" value={settings?.name || ''} onChange={e => setSettings({...settings, name: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" />
              </div>
              <div className="space-y-2">
                <Label>Descripcion</Label>
                <Input data-testid="settings-description" value={settings?.description || ''} onChange={e => setSettings({...settings, description: e.target.value})} placeholder="Breve descripcion de tu inmobiliaria" className="bg-black/20 border-white/10 focus:border-primary" />
              </div>
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-3">
                  <Input data-testid="settings-logo" value={settings?.logo_url || ''} onChange={e => setSettings({...settings, logo_url: e.target.value})} placeholder="https://... o sube un archivo" className="bg-black/20 border-white/10 focus:border-primary flex-1" />
                  <label className="shrink-0 cursor-pointer px-3 py-2 rounded-md bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors border border-primary/20" data-testid="upload-logo-btn">
                    Subir
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const r = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setSettings({...settings, logo_url: r.data.url});
                        toast.success('Logo subido');
                      } catch (err) { toast.error('Error al subir logo'); }
                    }} />
                  </label>
                </div>
              </div>
              {settings?.logo_url && (
                <div className="inline-flex items-center gap-3 p-2 rounded-lg border border-white/10" style={{ backgroundImage: 'linear-gradient(45deg,#2a2a2a 25%,transparent 25%),linear-gradient(-45deg,#2a2a2a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#2a2a2a 75%),linear-gradient(-45deg,transparent 75%,#2a2a2a 75%)', backgroundSize: '16px 16px', backgroundPosition: '0 0,0 8px,8px -8px,-8px 0' }} data-testid="logo-preview-wrapper">
                  <img src={settings.logo_url.startsWith('http') ? settings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} alt="Logo" className="h-12" data-testid="logo-preview" />
                  <span className="text-[10px] text-muted-foreground">Vista previa con transparencia</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email de contacto</Label>
                  <Input data-testid="settings-email" type="email" value={settings?.contact_email || ''} onChange={e => setSettings({...settings, contact_email: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">Telefono del negocio
                    <HelpTip title="Telefono del negocio" content={"Es el numero oficial de la empresa (el que publicas a clientes).\n\nHoy se usa en landing y contacto. Mas adelante la recepcionista enruta llamadas entrantes a este numero.\n\nUn solo numero por empresa. Formato internacional recomendado."} />
                  </Label>
                  <PhoneInput data-testid="settings-phone" value={settings?.contact_phone || ''} onChange={v => setSettings({...settings, contact_phone: v})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Direccion</Label>
                  <Input data-testid="settings-address" value={settings?.address || ''} onChange={e => setSettings({...settings, address: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
                <div className="space-y-2">
                  <Label>Sitio Web</Label>
                  <Input data-testid="settings-website" value={settings?.website || ''} onChange={e => setSettings({...settings, website: e.target.value})} placeholder="https://..." className="bg-black/20 border-white/10 focus:border-primary" />
                </div>
              </div>

              {/* Recepcionista — cimientos: zona horaria + horario de atencion (fail-open) */}
              <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4" data-testid="settings-horario-atencion">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="flex items-center gap-1.5 text-white">
                      Horario de atencion
                      <HelpTip title="Horario de atencion" content={"Define en que dias y horas atiende tu negocio.\n\nIMPORTANTE: si lo dejas apagado, el sistema asume que SIEMPRE estas abierto. Nadie se queda sin atencion por no haber llenado este dato.\n\nCuando la recepcionista de voz este lista: en horario enruta al vendedor de guardia; fuera de horario contesta la IA."} />
                    </Label>
                    <p className="text-[10px] text-muted-foreground mt-1">Apagado = siempre abierto (recomendado hasta configurarlo).</p>
                  </div>
                  <Switch
                    data-testid="switch-horario-enabled"
                    checked={Boolean(settings?.business_hours?.enabled)}
                    onCheckedChange={(v) => setSettings({
                      ...settings,
                      business_hours: {
                        enabled: v,
                        days: settings?.business_hours?.days || [0,1,2,3,4],
                        start: settings?.business_hours?.start || '09:00',
                        end: settings?.business_hours?.end || '18:00',
                      },
                    })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Zona horaria</Label>
                  <Select
                    value={settings?.timezone || 'America/Mexico_City'}
                    onValueChange={(v) => setSettings({ ...settings, timezone: v })}
                  >
                    <SelectTrigger data-testid="settings-timezone" className="bg-black/20 border-white/10">
                      <SelectValue placeholder="Zona horaria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Mexico_City">Ciudad de Mexico (CDMX)</SelectItem>
                      <SelectItem value="America/Monterrey">Monterrey</SelectItem>
                      <SelectItem value="America/Cancun">Cancun (Quintana Roo)</SelectItem>
                      <SelectItem value="America/Tijuana">Tijuana</SelectItem>
                      <SelectItem value="America/Hermosillo">Hermosillo</SelectItem>
                      <SelectItem value="America/Merida">Merida</SelectItem>
                      <SelectItem value="America/Chihuahua">Chihuahua</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {settings?.business_hours?.enabled && (
                  <>
                    <div className="space-y-2">
                      <Label>Dias</Label>
                      <div className="flex flex-wrap gap-2" data-testid="settings-horario-dias">
                        {[
                          [0, 'Lun'], [1, 'Mar'], [2, 'Mie'], [3, 'Jue'],
                          [4, 'Vie'], [5, 'Sab'], [6, 'Dom'],
                        ].map(([d, label]) => {
                          const days = settings?.business_hours?.days || [];
                          const on = days.includes(d);
                          return (
                            <button
                              key={d}
                              type="button"
                              data-testid={`horario-dia-${d}`}
                              onClick={() => {
                                const next = on ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b);
                                setSettings({
                                  ...settings,
                                  business_hours: { ...settings.business_hours, days: next.length ? next : [0,1,2,3,4] },
                                });
                              }}
                              className={`px-2.5 py-1 rounded-md text-xs border transition ${
                                on
                                  ? 'bg-primary/20 border-primary/40 text-white'
                                  : 'bg-black/20 border-white/10 text-muted-foreground'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Abre</Label>
                        <Input
                          type="time"
                          data-testid="settings-horario-start"
                          value={settings?.business_hours?.start || '09:00'}
                          onChange={(e) => setSettings({
                            ...settings,
                            business_hours: { ...settings.business_hours, start: e.target.value || '09:00' },
                          })}
                          className="bg-black/20 border-white/10 focus:border-primary"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cierra</Label>
                        <Input
                          type="time"
                          data-testid="settings-horario-end"
                          value={settings?.business_hours?.end || '18:00'}
                          onChange={(e) => setSettings({
                            ...settings,
                            business_hours: { ...settings.business_hours, end: e.target.value || '18:00' },
                          })}
                          className="bg-black/20 border-white/10 focus:border-primary"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* §B Ventana de decencia para llamadas de voz — la IA nunca llama de madrugada */}
              <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4" data-testid="settings-voz-decencia">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="flex items-center gap-1.5 text-white">
                      Horario para llamadas de voz
                      <HelpTip title="Horario para llamadas de voz" content={"Rango en el que la IA de voz PUEDE llamar a un lead.\n\nSi una llamada programada cae FUERA de este rango (por ejemplo de madrugada), NO se hace: se agenda para la manana siguiente, para no molestar al lead.\n\nSolo afecta a las llamadas de voz. WhatsApp y correo no cambian. Recomendado 9:00 a 21:00. Aplica a cualquier vertical."} />
                    </Label>
                    <p className="text-[10px] text-muted-foreground mt-1">Apagado = la IA puede llamar a cualquier hora (no recomendado).</p>
                  </div>
                  <Switch
                    data-testid="switch-voz-decencia-enabled"
                    checked={settings?.voice_quiet_hours?.enabled !== false}
                    onCheckedChange={(v) => setSettings({
                      ...settings,
                      voice_quiet_hours: {
                        enabled: v,
                        days: settings?.voice_quiet_hours?.days || [0,1,2,3,4,5,6],
                        start: settings?.voice_quiet_hours?.start || '09:00',
                        end: settings?.voice_quiet_hours?.end || '21:00',
                      },
                    })}
                  />
                </div>
                {(settings?.voice_quiet_hours?.enabled !== false) && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Desde</Label>
                      <Input
                        type="time"
                        data-testid="settings-voz-decencia-start"
                        value={settings?.voice_quiet_hours?.start || '09:00'}
                        onChange={(e) => setSettings({ ...settings, voice_quiet_hours: { ...(settings?.voice_quiet_hours || {}), enabled: true, start: e.target.value || '09:00' } })}
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hasta</Label>
                      <Input
                        type="time"
                        data-testid="settings-voz-decencia-end"
                        value={settings?.voice_quiet_hours?.end || '21:00'}
                        onChange={(e) => setSettings({ ...settings, voice_quiet_hours: { ...(settings?.voice_quiet_hours || {}), enabled: true, end: e.target.value || '21:00' } })}
                        className="bg-black/20 border-white/10 focus:border-primary"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          {/* Branding */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Palette className="w-4 h-4 text-secondary" />Branding</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Colores de tu marca para landing pages, botones y elementos del CRM</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Color Primario</Label>
                  <p className="text-[10px] text-muted-foreground">Botones, CTAs, enlaces activos</p>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings?.primary_color || '#A5D62C'} onChange={e => setSettings({...settings, primary_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer border-0" data-testid="settings-primary-color" />
                    <Input value={settings?.primary_color || ''} onChange={e => setSettings({...settings, primary_color: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary font-mono text-sm" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Color Secundario</Label>
                  <p className="text-[10px] text-muted-foreground">Acentos, badges, iconos</p>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings?.secondary_color || '#1B5E7B'} onChange={e => setSettings({...settings, secondary_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer border-0" data-testid="settings-secondary-color" />
                    <Input value={settings?.secondary_color || ''} onChange={e => setSettings({...settings, secondary_color: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary font-mono text-sm" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Color Terciario</Label>
                  <p className="text-[10px] text-muted-foreground">Fondos, secciones, detalles</p>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings?.tertiary_color || '#0B0C10'} onChange={e => setSettings({...settings, tertiary_color: e.target.value})} className="w-10 h-10 rounded cursor-pointer border-0" data-testid="settings-tertiary-color" />
                    <Input value={settings?.tertiary_color || ''} onChange={e => setSettings({...settings, tertiary_color: e.target.value})} className="bg-black/20 border-white/10 focus:border-primary font-mono text-sm" />
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-black/20 border border-white/5">
                <p className="text-[10px] text-muted-foreground">
                  <strong className="text-white">Primario:</strong> Se usa en botones de accion, enlaces y elementos interactivos. &nbsp;
                  <strong className="text-white">Secundario:</strong> Para acentos visuales, iconos y badges. &nbsp;
                  <strong className="text-white">Terciario:</strong> Fondos de secciones, footer y detalles decorativos en la landing page.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Duplicate Lead Detection */}
          {/* Duplicate Lead Detection — movido a Team Config */}
          <Card className="bg-card/40 backdrop-blur-md border-amber-500/15" data-testid="duplicate-detection-moved">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-400" /> Detección de Leads Duplicados
              </CardTitle>
              <CardDescription className="text-muted-foreground text-xs">
                Esta configuración se movió a <strong className="text-white">Team Config → Detección de Duplicados</strong>. Allí podrás activar el modo, configurar el alcance (empresa/marca/producto), ventana de tiempo y acción.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RouterLink to="/team-config?tab=duplicados">
                <Button variant="outline" className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10 text-xs h-9" data-testid="duplicate-detection-goto">
                  Abrir en Team Config →
                </Button>
              </RouterLink>
            </CardContent>
          </Card>

          <Card className="bg-card/40 backdrop-blur-md border-violet-500/15" data-testid="calificaciones-forecast-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                Calificaciones Forecast
              </CardTitle>
              <CardDescription className="text-muted-foreground text-xs">
                Las calificaciones del panel de Leads son CEM (interés del prospecto), no las etapas del pipeline.
                Aquí se editan, se ponderan 1–10 (seller score) y se carga la meta mensual del forecast.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <a href="/settings/calificaciones-forecast">
                <Button variant="outline" className="border-violet-500/30 text-violet-200 hover:bg-violet-500/10 text-xs h-9" data-testid="calificaciones-forecast-goto">
                  Abrir Calificaciones Forecast →
                </Button>
              </a>
            </CardContent>
          </Card>

          {(impersonating || allowedModules.capi === true) && (
          <Card className="bg-card/40 backdrop-blur-md border-cyan-500/15" data-testid="capi-settings-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400" /> CAPI Meta
              </CardTitle>
              <CardDescription className="text-muted-foreground text-xs">
                Le decimos a Meta cómo avanzan tus leads (contacto → cita → venta) para que te mande mejores.
                Guía incluida. Nace apagado.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RouterLink to="/settings/capi">
                <Button variant="outline" className="border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10 text-xs h-9" data-testid="capi-settings-goto">
                  Abrir CAPI →
                </Button>
              </RouterLink>
            </CardContent>
          </Card>
          )}

          {/* Landing Page Config */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="landing-page-config">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Globe className="w-4 h-4 text-blue-400" />Landing Page Publica</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Configura tu pagina publica para mostrar propiedades a clientes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                <div>
                  <p className="text-sm text-white">Activar Landing Page</p>
                  <p className="text-[10px] text-muted-foreground">Habilita tu pagina publica con tus propiedades y formulario de contacto</p>
                </div>
                <button
                  onClick={() => setSettings({...settings, landing_enabled: !settings?.landing_enabled})}
                  className={`w-11 h-6 rounded-full transition-all relative ${settings?.landing_enabled ? 'bg-emerald-500' : 'bg-white/10'}`}
                  data-testid="landing-toggle"
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${settings?.landing_enabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>

              {settings?.landing_enabled && (
                <>
                  <div className="space-y-2">
                    <Label className="text-xs">URL de tu landing <span className="text-destructive">*</span></Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground shrink-0">{window.location.origin}/a/</span>
                      <Input
                        data-testid="landing-slug"
                        value={settings?.slug || ''}
                        onChange={e => setSettings({...settings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                        placeholder="mi-inmobiliaria"
                        className="bg-black/20 border-white/10 focus:border-blue-400 font-mono text-sm"
                      />
                    </div>
                    {settings?.slug && (
                      <div className="flex items-center gap-2">
                        <a href={`${window.location.origin}/a/${settings.slug}`} target="_blank" rel="noreferrer" className="text-[11px] text-blue-400 hover:underline font-mono" data-testid="landing-preview-link">
                          {window.location.origin}/a/{settings.slug}
                        </a>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/a/${settings.slug}`); toast.success('URL copiada'); }}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Titulo principal</Label>
                    <Input
                      data-testid="landing-headline"
                      value={settings?.landing_headline || ''}
                      onChange={e => setSettings({...settings, landing_headline: e.target.value})}
                      placeholder={`Encuentra tu propiedad ideal con ${settings?.name || 'tu inmobiliaria'}`}
                      className="bg-black/20 border-white/10 focus:border-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Descripcion</Label>
                    <Input
                      data-testid="landing-description"
                      value={settings?.landing_description || ''}
                      onChange={e => setSettings({...settings, landing_description: e.target.value})}
                      placeholder="Explora nuestro catalogo de propiedades..."
                      className="bg-black/20 border-white/10 focus:border-primary"
                    />
                  </div>

                  {/* QR Code for Landing Page */}
                  {settings?.slug && (
                    <div className="p-4 rounded-lg bg-black/20 border border-white/5" data-testid="landing-qr-section">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-3 flex items-center gap-1.5">
                        <QrCode className="w-3 h-3" /> Codigo QR de tu Landing Page
                      </p>
                      <p className="text-[10px] text-muted-foreground mb-3">Descarga este QR para usarlo en materiales impresos (flyers, espectaculares, tarjetas).</p>
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-lg" data-testid="landing-qr-code">
                          <QRCodeSVG
                            id="landing-qr-svg"
                            value={`${window.location.origin}/a/${settings.slug}`}
                            size={140}
                            level="H"
                            fgColor={settings?.primary_color || '#000000'}
                          />
                        </div>
                        <div className="flex-1 space-y-2">
                          <p className="text-[11px] text-white font-mono break-all">{window.location.origin}/a/{settings.slug}</p>
                          <Button
                            onClick={downloadQr}
                            variant="outline"
                            size="sm"
                            className="border-white/10 text-xs"
                            data-testid="download-qr-btn"
                          >
                            <Download className="w-3 h-3 mr-1.5" /> Descargar QR (.png)
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* A/B Testing for Landing Page */}
          {settings?.landing_enabled && (
            <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="ab-testing-card">
              <CardHeader className="pb-4">
                <CardTitle className="font-heading text-base text-white flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-400" />A/B Testing</CardTitle>
                <CardDescription className="text-muted-foreground text-xs">Crea variantes de tu landing page para probar que titulo convierte mejor</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {abVariants.length > 0 && (
                  <div className="space-y-2">
                    {abVariants.map(v => (
                      <div key={v.id} className="p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`ab-variant-${v.id}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white font-medium">{v.name}</span>
                            <Badge variant="outline" className={`text-[9px] border-white/10 ${v.is_active ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                              {v.is_active ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => toggleAbVariant(v.id, !v.is_active)}
                              className={`w-8 h-4 rounded-full transition-all relative ${v.is_active ? 'bg-emerald-500' : 'bg-white/10'}`}
                              data-testid={`ab-toggle-${v.id}`}
                            >
                              <div className={`w-3 h-3 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${v.is_active ? 'left-[17px]' : 'left-0.5'}`} />
                            </button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive" onClick={() => deleteAbVariant(v.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground truncate mb-1">{v.headline}</p>
                        <div className="flex gap-4 text-[10px]">
                          <span className="text-muted-foreground">Vistas: <strong className="text-white">{v.views || 0}</strong></span>
                          <span className="text-muted-foreground">Conversiones: <strong className="text-emerald-400">{v.conversions || 0}</strong></span>
                          <span className="text-muted-foreground">Tasa: <strong className="text-violet-400">{v.views > 0 ? ((v.conversions / v.views) * 100).toFixed(1) : '0'}%</strong></span>
                          {v.significant && <Badge className="bg-emerald-500/20 text-emerald-400 text-[8px] border-0">Significativa</Badge>}
                          {v.confidence_interval > 0 && <span className="text-[9px] text-muted-foreground/60">CI: ±{v.confidence_interval}%</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2 p-3 rounded-lg bg-black/20 border border-white/5">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Nueva Variante</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="ab-name" value={abForm.name} onChange={e => setAbForm({ ...abForm, name: e.target.value })} placeholder="Nombre (ej: Version A)" className="bg-black/30 border-white/10 text-xs" />
                    <Input data-testid="ab-cta" value={abForm.cta_text} onChange={e => setAbForm({ ...abForm, cta_text: e.target.value })} placeholder="Texto CTA" className="bg-black/30 border-white/10 text-xs" />
                  </div>
                  <Input data-testid="ab-headline" value={abForm.headline} onChange={e => setAbForm({ ...abForm, headline: e.target.value })} placeholder="Titulo alternativo *" className="bg-black/30 border-white/10 text-xs" />
                  <Input data-testid="ab-description" value={abForm.description} onChange={e => setAbForm({ ...abForm, description: e.target.value })} placeholder="Descripcion alternativa" className="bg-black/30 border-white/10 text-xs" />
                  <Button onClick={createAbVariant} disabled={abCreating} className="w-full bg-violet-600 hover:bg-violet-500 text-white text-xs h-8" data-testid="create-ab-variant-btn">
                    {abCreating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                    Crear Variante
                  </Button>
                </div>
                <div className="p-2.5 rounded-lg bg-violet-500/5 border border-violet-500/10">
                  <p className="text-[10px] text-violet-400/80">
                    Las variantes activas se muestran aleatoriamente a visitantes de tu landing page. Las vistas y conversiones se registran automaticamente.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== TAB: IA ==================== */}
        <TabsContent value="ia" className="space-y-6">
          <div className="flex flex-wrap gap-1 p-1 rounded-lg border border-white/10 bg-black/20" data-testid="ia-subtabs">
            <button
              type="button"
              onClick={() => {
                setIaSub('cerebro');
                const u = new URL(window.location.href);
                u.searchParams.set('tab', 'ia');
                u.searchParams.delete('sub');
                window.history.replaceState({}, '', u.toString());
              }}
              className={`text-xs px-3 py-1.5 rounded-md ${iaSub === 'cerebro' ? 'bg-[#A5D62C]/20 text-[#A5D62C]' : 'text-muted-foreground'}`}
              data-testid="ia-subtab-cerebro"
            >
              Cerebro del asistente
            </button>
            <button
              type="button"
              onClick={() => {
                setIaSub('campanas');
                const u = new URL(window.location.href);
                u.searchParams.set('tab', 'ia');
                u.searchParams.set('sub', 'campanas');
                window.history.replaceState({}, '', u.toString());
              }}
              className={`text-xs px-3 py-1.5 rounded-md ${iaSub === 'campanas' ? 'bg-violet-500/20 text-violet-300' : 'text-muted-foreground'}`}
              data-testid="ia-subtab-campanas"
            >
              Campañas de este asistente
            </button>
          </div>
          {iaSub === 'campanas' ? (
            <AICampaignIndependentPage embedded />
          ) : null}
          <div className={iaSub === 'campanas' ? 'hidden' : 'space-y-6'}>
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="ai-citas-config-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-violet-300" />Citas automáticas</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Define si la IA agenda, confirma y reagenda citas por WhatsApp y voz: duración, cupos por horario y horario de atención.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4" data-testid="appointment-config-section">
                    

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-white">Activar agendamiento de citas</p>
                        <p className="text-[10px] text-muted-foreground">
                          La IA podrá agendar citas y pruebas de manejo directamente en WhatsApp
                        </p>
                      </div>
                      <Switch
                        checked={!!agentConfig.appointment_config?.enabled}
                        onCheckedChange={(val) =>
                          setAgentConfig(prev => ({
                            ...prev,
                            appointment_config: { ...prev.appointment_config, enabled: val }
                          }))
                        }
                        data-testid="appointment-config-enabled-toggle"
                        disabled={agentSaving}
                      />
                    </div>

                    {agentConfig.appointment_config?.enabled && (
                      <div className="space-y-4 pl-1">
                        <div className="space-y-1">
                          <Label className="text-[11px] text-white">Duración de cada cita (minutos)</Label>
                          <select
                            className="w-full border border-white/10 rounded px-3 py-2 text-xs bg-black/30 text-white"
                            value={agentConfig.appointment_config?.slot_duration_minutes || 60}
                            onChange={(e) =>
                              setAgentConfig(prev => ({
                                ...prev,
                                appointment_config: {
                                  ...prev.appointment_config,
                                  slot_duration_minutes: parseInt(e.target.value)
                                }
                              }))
                            }
                            data-testid="appointment-config-slot-duration"
                            disabled={agentSaving}
                          >
                            <option value={30}>30 minutos</option>
                            <option value={60}>1 hora</option>
                            <option value={90}>1.5 horas</option>
                            <option value={120}>2 horas</option>
                          </select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-[11px] text-white">Citas simultáneas por horario</Label>
                          <select
                            className="w-full border border-white/10 rounded px-3 py-2 text-xs bg-black/30 text-white"
                            value={agentConfig.appointment_config?.max_simultaneous || 1}
                            onChange={(e) =>
                              setAgentConfig(prev => ({
                                ...prev,
                                appointment_config: {
                                  ...prev.appointment_config,
                                  max_simultaneous: parseInt(e.target.value)
                                }
                              }))
                            }
                            data-testid="appointment-config-max-simultaneous"
                            disabled={agentSaving}
                          >
                            <option value={1}>1 (sin traslapes)</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-[11px] text-white">Horario de atención</Label>
                          {[
                            { key: 'monday',    label: 'Lunes' },
                            { key: 'tuesday',   label: 'Martes' },
                            { key: 'wednesday', label: 'Miércoles' },
                            { key: 'thursday',  label: 'Jueves' },
                            { key: 'friday',    label: 'Viernes' },
                            { key: 'saturday',  label: 'Sábado' },
                            { key: 'sunday',    label: 'Domingo' },
                          ].map(({ key, label }) => {
                            const dayConfig = agentConfig.appointment_config?.schedule?.[key];
                            const isOpen = !!dayConfig;
                            return (
                              <div key={key} className="flex items-center gap-3" data-testid={`appointment-config-day-${key}`}>
                                <Switch
                                  checked={isOpen}
                                  onCheckedChange={(val) =>
                                    setAgentConfig(prev => ({
                                      ...prev,
                                      appointment_config: {
                                        ...prev.appointment_config,
                                        schedule: {
                                          ...prev.appointment_config.schedule,
                                          [key]: val ? { open: '09:00', close: '18:00' } : null
                                        }
                                      }
                                    }))
                                  }
                                  data-testid={`appointment-config-day-${key}-toggle`}
                                  disabled={agentSaving}
                                />
                                <span className="text-xs w-20 text-white">{label}</span>
                                {isOpen && (
                                  <>
                                    <input
                                      type="time"
                                      className="border border-white/10 rounded px-2 py-1 text-xs bg-black/30 text-white"
                                      value={dayConfig.open}
                                      onChange={(e) =>
                                        setAgentConfig(prev => ({
                                          ...prev,
                                          appointment_config: {
                                            ...prev.appointment_config,
                                            schedule: {
                                              ...prev.appointment_config.schedule,
                                              [key]: { ...dayConfig, open: e.target.value }
                                            }
                                          }
                                        }))
                                      }
                                      data-testid={`appointment-config-day-${key}-open`}
                                      disabled={agentSaving}
                                    />
                                    <span className="text-[10px] text-muted-foreground">a</span>
                                    <input
                                      type="time"
                                      className="border border-white/10 rounded px-2 py-1 text-xs bg-black/30 text-white"
                                      value={dayConfig.close}
                                      onChange={(e) =>
                                        setAgentConfig(prev => ({
                                          ...prev,
                                          appointment_config: {
                                            ...prev.appointment_config,
                                            schedule: {
                                              ...prev.appointment_config.schedule,
                                              [key]: { ...dayConfig, close: e.target.value }
                                            }
                                          }
                                        }))
                                      }
                                      data-testid={`appointment-config-day-${key}-close`}
                                      disabled={agentSaving}
                                    />
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
              <div className="flex justify-end pt-2">
                <Button size="sm" onClick={() => saveAgentConfig()} disabled={agentSaving} className="bg-violet-600 hover:bg-violet-500 text-white text-xs" data-testid="citas-config-save-btn">{agentSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Guardar citas</Button>
              </div>
            </CardContent>
          </Card>


          {/* AI Sales Agent Config */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="ai-agent-config-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><UserCheck className="w-4 h-4 text-emerald-400" />Agente IA de Ventas <HelpTip title="Activar el agente" content={"Con esto encendido, la IA responde sola los WhatsApp que entran.\n\nLa IA la paga LeadCEM (OpenRouter de plataforma). No hace falta pegar una llave aqui.\n\nApagarlo NO borra nada: las conversaciones quedan y el equipo sigue atendiendo a mano."} /><HelpTip title="Agente IA" content="Aquí entrenas al asistente, defines su base de conocimiento, llamadas, takeover humano y funciones disponibles por modelo/proveedor." /></CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Agente automatizado que conversa con leads via WhatsApp usando tecnicas de venta profesionales</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                <div>
                  <p className="text-sm text-white font-medium">Activar Agente IA</p>
                  <p className="text-[10px] text-muted-foreground">Responde automaticamente a mensajes de WhatsApp entrantes</p>
                </div>
                <Switch
                  checked={agentConfig?.enabled || false}
                  onCheckedChange={(checked) => {
                    saveAgentConfig({ enabled: checked });
                  }}
                  data-testid="ai-agent-toggle"
                />
              </div>
              <div className="flex items-start gap-3 mt-4 p-3 rounded-lg border border-white/10 bg-black/10" data-testid="agente-memoria">
                <Switch
                  checked={agentConfig?.memoria_conversacion !== false}
                  onCheckedChange={(v) => saveAgentConfig({ memoria_conversacion: v })}
                  data-testid="switch-memoria-conversacion"
                />
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Memoria de conversación</Label>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Encendida (por defecto), la IA lee el historial del lead —llamadas y citas previas— para dar continuidad, por ejemplo para confirmar o reprogramar una cita. Apágala si prefieres que cada conversación empiece de cero.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 mt-4 p-3 rounded-lg border border-white/10 bg-black/10" data-testid="agente-herramientas">
                <Switch
                  checked={agentConfig?.usar_herramientas || false}
                  onCheckedChange={(v) => saveAgentConfig({ usar_herramientas: v })}
                  data-testid="switch-usar-herramientas"
                />
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Que la IA ejecute, no solo escriba</Label>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Encendido, la IA manda fichas, fotos y agenda citas llamando de verdad al
                    sistema, y sabe si funciono. Apagado, lo intenta escribiendo una instruccion
                    dentro de su mensaje: si se equivoca al escribirla no pasa nada y ella no se
                    entera, asi que puede decirle al cliente que envio algo que nunca salio.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 mt-4 p-3 rounded-lg border border-white/10 bg-black/10" data-testid="agente-voz-entiende">
                <Switch
                  checked={!!agentConfig?.voz_wa?.entender_notas}
                  onCheckedChange={(v) => saveAgentConfig({ voz_wa: { ...(agentConfig?.voz_wa || {}), entender_notas: v } })}
                  data-testid="switch-voz-entender"
                />
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Entender notas de voz de los leads</Label>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    Encendido, cuando un lead manda una nota de voz por WhatsApp, la IA la transcribe y la entiende como si fuera texto. Apagado, la nota de voz se ignora (como hoy).
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 mt-4 p-3 rounded-lg border border-white/10 bg-black/10" data-testid="agente-voz-responde">
                <Switch
                  checked={!!agentConfig?.voz_wa?.responder_nota && !!agentConfig?.tiene_voz_ia}
                  disabled={!agentConfig?.tiene_voz_ia}
                  onCheckedChange={(v) => saveAgentConfig({ voz_wa: { ...(agentConfig?.voz_wa || {}), responder_nota: v } })}
                  data-testid="switch-voz-responder"
                />
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Responder con nota de voz</Label>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    {agentConfig?.tiene_voz_ia
                      ? 'Encendido, la IA responde con una nota de voz (su misma voz) en vez de texto. Apagado, responde por texto.'
                      : 'Requiere tener Voz IA en tu plan. Sin Voz IA, la IA siempre responde por texto.'}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Nombre del Agente<HelpTip title="Nombre del Agente" content={"Con este nombre se presenta la IA al escribirle a un prospecto. Lo tienen las 25 empresas.\n\nInmobiliaria: \"Soy Bianca, asesora inmobiliaria\".\nAutomotriz: \"Soy Diego, de Chevrolet Cuautla\".\nColegio: \"Soy Ana, de admisiones\".\n\nSi lo dejas vacio, la IA se presenta de forma generica y suena a robot."} /></Label>
                <Input
                  value={agentConfig?.agent_name || 'Ana'}
                  onChange={e => setAgentConfig({ ...agentConfig, agent_name: e.target.value })}
                  placeholder="Nombre que usara el agente"
                  className="bg-black/20 border-white/10 focus:border-primary"
                  data-testid="ai-agent-name"
                />
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="ai-agent-capabilities-card"><HelpTip title="Capacidades del proveedor" content={"No se configura: es informativo. Muestra que puede hacer la IA con el proveedor de IA de tu plan.\n\nSi cambias de modelo, esta lista cambia. Sirve para saber por que una funcion no aparece: no es que falle, es que ese proveedor no la ofrece."} />
                <div>
                  <p className="text-xs text-white font-semibold">Capacidades disponibles segun IA / proveedor</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Aqui se muestra lo que el sistema puede ejecutar con el proveedor de IA de tu plan.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                  <div className="p-3 rounded-lg bg-black/20 border border-white/5">
                    <p className="text-white font-medium">Llamadas IA salientes</p>
                    <p className="text-muted-foreground mt-1">Disponibles usando Twilio Voice como sender y el prompt de llamadas configurado abajo.</p>
                  </div>
                  <div className="p-3 rounded-lg bg-black/20 border border-white/5">
                    <p className="text-white font-medium">Voz del asistente</p>
                    <p className="text-muted-foreground mt-1">Actualmente la salida usa voz de Twilio tipo femenina en español. La siguiente fase permitirá selector más fino por perfil/voz.</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Telefono fallback de supervision<HelpTip title="Telefono de supervision" content={"Numero al que se avisa cuando algo no se pudo entregar al vendedor asignado: no contesta, no tiene WhatsApp, o el lead quedo sin asignar.\n\nHoy lo tienen 8 de 25 empresas. Sin el, esos avisos no llegan a nadie y el lead se enfria sin que te enteres.\n\nSuele ser el celular del gerente comercial o de recepcion."} /></Label>
                <PhoneInput
                  value={agentConfig?.notify_phone || ''}
                  onChange={v => setAgentConfig({ ...agentConfig, notify_phone: v })}
                  placeholder="Numero de respaldo / supervision"
                  data-testid="ai-agent-notify-phone"
                />
                <p className="text-[10px] text-muted-foreground">La alerta principal debe ir al vendedor asignado por ruleta. Este numero queda como respaldo de supervision cuando no exista asignacion valida.</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="ai-agent-function-map"><HelpTip title="Funciones del CEM" content={"Informativo. Es el recorrido que sigue un lead desde que escribe hasta que un vendedor lo atiende.\n\nLo importante que dice: la ruleta asigna al vendedor PRIMERO, y la IA solo trabaja sobre esa asignacion. Si un lead llego a quien no debia, el problema esta en Reglas de Asignacion, no aqui."} />
                <div>
                  <p className="text-xs text-white font-semibold">Funciones activas del CEM</p>
                  <p className="text-[10px] text-muted-foreground mt-1">La ruleta asigna vendedor primero; la IA solo ejecuta funciones sobre esa asignacion.</p>
                </div>
                <div className="space-y-2 text-[11px] text-white/75">
                  <p>• Lead interactuando con IA → alerta WhatsApp al vendedor asignado con super link a la conversacion</p>
                  <p>• Lead quiere humano → llamada bilateral Twilio Voice con el vendedor asignado</p>
                  <p>• Vendedor toma control → responde desde vista movil dinamica del Agente IA</p>
                  <p>• Envio de ficha → desde takeover, usando la propiedad ligada al lead</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Mensaje de espera (al conectar con un asesor)<HelpTip title="Mensaje de llamadas" content={"SOLO para el telefono. Es la frase de ESPERA que se dice al CONECTAR la llamada con un asesor. NO es lo que la IA dice al contestar una llamada entrante (eso lo controla el saludo del asistente, mas abajo). NO afecta a WhatsApp.\n\nESTRUCTURA: una o dos frases, sin listas ni formato. Se va a LEER en voz alta, asi que escribelo como se habla.\n\n  1. Saludo con el nombre del negocio\n  2. Que va a pasar ahora\n\nAutomotriz: \"Gracias por llamar a Chevrolet Cuautla. Te comunico con un asesor de seminuevos, un momento por favor.\"\nInmobiliaria: \"Gracias por tu interes en Residencial Agwa. Te paso con la asesora del desarrollo.\"\n\nEvita numeros largos, correos y direcciones: la voz los lee mal.\nLo tienen 21 de 25 empresas."} /></Label>
                <Textarea
                  value={agentConfig?.call_prompt_template || 'Conectando tu llamada. Por favor espera.'}
                  onChange={e => setAgentConfig({ ...agentConfig, call_prompt_template: e.target.value })}
                  placeholder="Mensaje que la IA dira al iniciar una llamada o conectar al asesor"
                  className="bg-black/20 border-white/10 focus:border-primary min-h-[80px]"
                  data-testid="ai-agent-call-prompt"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Propósito de las llamadas SALIENTES<HelpTip title="Proposito de salida" content="Cuando la IA es quien LLAMA al lead (no cuando contesta), este es el motivo por el que llama. Lo dice con SUS palabras despues de presentarse: Hola [nombre], te habla [asistente] de [empresa], te llamo para... Escribelo como el final de la frase 'Te llamo para ...'. Ejemplo: dar seguimiento a tu interes y ver en que te puedo apoyar. Si una receta o campana trae un motivo especifico para esa llamada, ese manda; este es el motivo por defecto. Es distinto del mensaje de arriba (ese se dice al CONECTAR con un asesor). Aplica a cualquier vertical." /></Label>
                <Textarea
                  value={agentConfig?.outbound_call_intro ?? 'dar seguimiento a tu interes y ver en que te puedo apoyar'}
                  onChange={e => setAgentConfig({ ...agentConfig, outbound_call_intro: e.target.value })}
                  placeholder="Te llamo para..."
                  className="bg-black/20 border-white/10 focus:border-primary min-h-[70px]"
                  data-testid="ai-agent-outbound-intro"
                />
                <div className="flex flex-wrap items-center gap-1 pt-0.5" data-testid="vars-outbound">
                  <span className="text-[10px] text-muted-foreground mr-1">Insertar:</span>
                  {(agentConfig?.voice_variables || [{token:'{nombre}',label:'Nombre del lead'},{token:'{empresa}',label:'Nombre del negocio'}]).map(v => (
                    <button key={v.token} type="button" title={`Inserta ${v.label}`} onClick={() => setAgentConfig({ ...agentConfig, outbound_call_intro: ((agentConfig?.outbound_call_intro ?? '') + v.token) })} className="text-[10px] px-2 py-0.5 rounded-md bg-violet-500/10 border border-violet-500/20 text-violet-300 hover:bg-violet-500/20 transition-colors">+ {v.label}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Saludo / propósito de las llamadas ENTRANTES<HelpTip title="Contexto de entrada" content="Cuando el lead te LLAMA (no cuando la IA llama), la IA ya agradece que se comunico y pregunta en que ayudar. Aqui puedes dar contexto de por que suele llamar la gente, para que la IA lo tenga presente. Se PARAFRASEA, no se lee literal. Ejemplo: informar sobre autos seminuevos y agendar prueba de manejo. Dejalo vacio si no hace falta." /></Label>
                <Textarea
                  value={agentConfig?.inbound_call_intro ?? ''}
                  onChange={e => setAgentConfig({ ...agentConfig, inbound_call_intro: e.target.value })}
                  placeholder="Contexto de por que suele llamar la gente (opcional)"
                  className="bg-black/20 border-white/10 focus:border-primary min-h-[70px]"
                  data-testid="ai-agent-inbound-intro"
                />
                <div className="flex flex-wrap items-center gap-1 pt-0.5" data-testid="vars-inbound">
                  <span className="text-[10px] text-muted-foreground mr-1">Insertar:</span>
                  {(agentConfig?.voice_variables || [{token:'{nombre}',label:'Nombre del lead'},{token:'{empresa}',label:'Nombre del negocio'}]).map(v => (
                    <button key={v.token} type="button" title={`Inserta ${v.label}`} onClick={() => setAgentConfig({ ...agentConfig, inbound_call_intro: ((agentConfig?.inbound_call_intro ?? '') + v.token) })} className="text-[10px] px-2 py-0.5 rounded-md bg-violet-500/10 border border-violet-500/20 text-violet-300 hover:bg-violet-500/20 transition-colors">+ {v.label}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2" data-testid="ai-agent-callmode-wrap">
                <Label className="flex items-center gap-1.5">Como se atiende la llamada ENTRANTE<HelpTip title="Como se atiende la llamada" content="Cuando alguien LLAMA a tu numero: enlazar directo al vendedor, que la IA atienda, o Asistencia Fuera de Horario (la IA atiende cuando no hay nadie en la ruleta). Esta misma opcion decide TAMBIEN quien llama cuando una conversacion de chat dispara una llamada: con Llamada de la IA llama la IA, si no se enlaza al vendedor. Es una funcion del asistente, no por campana." /></Label>
                <div className="grid grid-cols-1 gap-1.5">
                  {[
                    { id: 'enlace_vendedor', label: 'Enlace al Vendedor', desc: 'La llamada entra y se enlaza directo al vendedor de la ruleta, sin pasar por la IA.', cap: null },
                    { id: 'ia', label: 'Llamada de la IA', desc: 'La IA atiende, da la informacion y asigna un vendedor que podra escuchar la grabacion y ver la transcripcion.', cap: 'voice_ai_inbound' },
                    { id: 'fuera_horario', label: 'Asistencia Fuera de Horario', desc: 'La IA atiende cuando no hay nadie en la ruleta y agenda el seguimiento, respetando la ruleta.', cap: 'voice_ai_inbound' },
                  ].map(md => {
                    const bloqueado = md.cap && !planCaps[md.cap];
                    return (
                    <button key={md.id} type="button" disabled={bloqueado} title={bloqueado ? 'Requiere un plan con IA de voz' : md.desc} onClick={() => { if (!bloqueado) setAgentConfig({ ...agentConfig, call_mode: md.id }); }} className={`px-3 py-2 rounded-lg text-[11px] font-medium border text-left transition-all ${bloqueado ? 'opacity-40 cursor-not-allowed bg-black/20 border-white/5 text-muted-foreground' : agentConfig?.call_mode === md.id ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/10'}`} data-testid={`agent-callmode-${md.id}`}>{md.label}{bloqueado && <span className="ml-1 text-[8px] px-1 rounded bg-white/5">requiere plan con IA de voz</span>}</button>
                    );
                  })}
                </div>
                {agentConfig?.call_mode === 'fuera_horario' && (
                  <button type="button" onClick={() => setAgentConfig({ ...agentConfig, call_ia_retry: !agentConfig?.call_ia_retry })} className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-[11px] font-medium border text-left transition-all ${agentConfig?.call_ia_retry ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/10'}`} data-testid="agent-ia-retry">
                    <span>Que la IA vuelva a intentar<span className="block text-[10px] font-normal text-muted-foreground leading-snug">Al abrir el horario, la IA re-llama al lead; si engancha y quiere hablar con alguien, lo pasa con el vendedor de turno. Apagado: enlace directo al vendedor.</span></span>
                    <span className={`shrink-0 inline-flex h-4 w-7 items-center rounded-full transition-all ${agentConfig?.call_ia_retry ? 'bg-amber-500/40' : 'bg-white/10'}`}><span className={`h-3 w-3 rounded-full bg-white transition-all ${agentConfig?.call_ia_retry ? 'translate-x-3.5' : 'translate-x-0.5'}`} /></span>
                  </button>
                )}
              </div>
              <div className="space-y-2" data-testid="ai-agent-reset-wrap">
                <Label className="flex items-center gap-1.5">Reiniciar la conversación tras inactividad<HelpTip title="Reiniciar la conversación" content="Si un cliente vuelve a escribir después de este tiempo sin actividad, la IA lo saluda de nuevo (arranca fresco) en vez de seguir el hilo viejo. En minutos. 0 = nunca reinicia." /></Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" max="10080" value={agentConfig?.conversation_reset_minutes ?? 0} onChange={e => setAgentConfig({ ...agentConfig, conversation_reset_minutes: Math.max(0, Math.min(10080, parseInt(e.target.value || "0", 10) || 0)) })} className="bg-black/20 border-white/10 text-sm w-28" data-testid="ai-agent-reset-minutes" />
                  <span className="text-xs text-muted-foreground">{(agentConfig?.conversation_reset_minutes > 0) ? `reinicia tras ${agentConfig.conversation_reset_minutes} min` : "minutos · 0 = nunca reinicia"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Prompt personalizado del asistente<HelpTip title="Instrucciones del asistente" content={"CUIDADO: esto NO se suma a las instrucciones de fabrica, las REEMPLAZA por completo.\n\nEl agente trae de fabrica 4.044 caracteres con la tecnica de venta y, sobre todo, las 8 instrucciones que le enseñan a mandar fichas, fotos, video y a agendar citas. Si aqui escribes solo \"no des precios\", pierdes TODO eso: el agente deja de saber enviar documentos.\n\nPor eso, si escribes aqui, escribe el prompt COMPLETO con esta estructura:\n\n  1. QUIEN ERES: \"Eres Ana, asesora de Casas KREA.\"\n  2. QUE VENDES: el producto y a quien.\n  3. COMO HABLAS: tono, tuteo o usted, largo de mensaje.\n  4. QUE NUNCA HACES: precios sin confirmar, fechas de entrega, etc.\n  5. QUE HACES AL FINAL: pedir el correo, ofrecer cita.\n  6. LAS ACCIONES que debe ejecutar: insertalas con el boton Insertar accion sobre el prompt; ya no escribes comandos tecnicos.\n\nSi solo quieres AGREGAR una regla sin perder lo de fabrica, ponla en Preguntas frecuentes, no aqui.\n\nLo usan 18 de 25 empresas y las 18 escribieron un prompt completo: es la forma correcta de usarlo.\n\nEl boton Restablecer Prompt vacia este campo y devuelve el de fabrica."} /></Label>
                <PromptAccionesEditor
                  value={agentConfig?.system_prompt_override || ''}
                  onChange={(v) => setAgentConfig({ ...agentConfig, system_prompt_override: v })}
                  skills={skillsCatalog}
                  activeSkills={(agentConfig && agentConfig.active_skills) || []}
                  onToggleSkill={(key, on) => {
                    const has = ((agentConfig && agentConfig.active_skills) || []).includes(key);
                    if ((on && !has) || (!on && has)) toggleAgentSkill(key);
                  }}
                  placeholder="Describe cómo debe actuar tu asistente…"
                />
                {isCarDealer && (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-300" data-testid="car-dealer-prompt-warning">
                    ⚠️ El inventario (modelos, precios, stock) se inyecta automáticamente desde tu Car-Dealer DMS. Si incluyes modelos en el prompt o FAQs, pueden generar conflictos con el inventario real del sistema.
                  </div>
                )}
                {(() => {
                  const _as = (agentConfig && agentConfig.active_skills) || [];
                  if (!_as.length) return (
                    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2 text-[11px] text-emerald-300" data-testid="ai-agent-skills-status">✓ Todas las acciones están activas. Si eliges una acción con “Insertar acción”, el asistente quedará limitado SOLO a las que elijas — las demás (enviar ficha, fotos, agendar, etc.) dejan de ejecutarse.</div>
                  );
                  const _lbls = _as.map(k => { const s = (skillsCatalog || []).find(x => x.key === k); return (s && s.label) || k; });
                  return (
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-300" data-testid="ai-agent-skills-status">⚠️ El asistente ejecutará SOLO estas {_as.length} acciones: {_lbls.join(', ')}. Las demás acciones de fábrica (enviar ficha, fotos, agendar, etc.) quedan apagadas mientras esta lista no esté vacía.</div>
                  );
                })()}
                <p className="text-[10px] text-amber-300" data-testid="ai-agent-prompt-override-warning">⚠️ Si escribes aquí, REEMPLAZA por completo el prompt de fábrica — no se suma. Déjalo vacío para usar el de fábrica. Para solo agregar reglas sin perder nada, usa Preguntas frecuentes (abajo).</p>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">FAQs / Base de conocimiento manual<HelpTip title="Preguntas frecuentes" content={"La forma SEGURA de enseñarle cosas a la IA: esto se SUMA a lo de fabrica, no reemplaza nada.\n\nESTRUCTURA: un par por bloque, una linea en blanco entre pares.\n\n  P: Aceptan creditos?\n  R: Si, trabajamos con Infonavit, Fovissste y bancos.\n\n  P: Cual es el enganche minimo?\n  R: Desde el 10%. El asesor te confirma segun el modelo.\n\nConsejo: escribe la pregunta COMO LA HACE EL CLIENTE, no como la diria un vendedor. \"Sale caro?\" funciona mejor que \"Politica de precios\".\n\nSolo 8 de 25 empresas lo usan y es lo que mas rapido mejora las respuestas. Si la IA contesta cosas imprecisas, empieza por aqui antes que por el prompt."} /></Label>
                <Textarea
                  value={agentConfig?.knowledge_faqs || ''}
                  onChange={e => setAgentConfig({ ...agentConfig, knowledge_faqs: e.target.value })}
                  placeholder={"P: ¿Aceptan creditos?\nR: Si, aceptamos creditos bancarios..."}
                  className="bg-black/20 border-white/10 focus:border-primary min-h-[120px]"
                  data-testid="ai-agent-faqs"
                />
                {isCatalog && (
                  <div className="space-y-2 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.05] p-3" data-testid="ai-agent-faq-items">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-emerald-200">FAQs con adjuntos (imágenes, PDF o video — no propiedades)</p>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-[10px]" onClick={addKnowledgeFaqItem} data-testid="ai-agent-add-faq-item">
                        <Plus className="w-3 h-3 mr-1" />Agregar FAQ
                      </Button>
                    </div>
                    {(agentConfig?.knowledge_faq_items || []).length === 0 && (
                      <p className="text-[10px] text-muted-foreground">Agrega una pregunta con material. La IA puede enviarlo con [ENVIAR_MEDIA_FAQ].</p>
                    )}
                    {(agentConfig?.knowledge_faq_items || []).map((item, idx) => (
                      <div key={item.id || idx} className="rounded-lg border border-white/10 bg-black/20 p-2 space-y-2" data-testid={`ai-agent-faq-item-${idx}`}>
                        <Input
                          value={item.question || ''}
                          onChange={(e) => updateKnowledgeFaqItem(idx, { ...item, question: e.target.value })}
                          placeholder="P: ¿Cuál es el temario?"
                          className="bg-black/20 border-white/10 text-xs"
                          data-testid={`ai-agent-faq-item-q-${idx}`}
                        />
                        <Textarea
                          value={item.answer || ''}
                          onChange={(e) => updateKnowledgeFaqItem(idx, { ...item, answer: e.target.value })}
                          placeholder="R: Incluye 8 sesiones y material digital..."
                          rows={2}
                          className="bg-black/20 border-white/10 text-xs"
                          data-testid={`ai-agent-faq-item-a-${idx}`}
                        />
                        <FaqMediaFields faq={item} onChange={(next) => updateKnowledgeFaqItem(idx, next)} testId={`ai-agent-faq-media-${idx}`} />
                        <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] text-red-300" onClick={() => removeKnowledgeFaqItem(idx)}>Quitar</Button>
                      </div>
                    ))}
                  </div>
                )}

              <FaqPropuestasPanel />
                {isCarDealer && (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-300" data-testid="car-dealer-faqs-warning">
                    ⚠️ El inventario (modelos, precios, stock) se inyecta automáticamente desde tu Car-Dealer DMS. Si incluyes modelos en el prompt o FAQs, pueden generar conflictos con el inventario real del sistema.
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">Sitio web de tu negocio (la IA lo lee para responder)<HelpTip title="Sitio web de consulta" content={"La IA lee este sitio y usa su contenido para responder.\n\nTIENE QUE SER UNA DIRECCION WEB COMPLETA, empezando por https://\n  Bien: https://casaskrea.com.mx/villas-san-fernando\n  Mal:  casaskrea.com  (falta https://)\n  Mal:  ventas@casaskrea.com  (eso es un correo)\n\nOJO: no todos los sitios se dejan leer, y el sistema ahora te lo dice.\n  - Si tu hosting tiene proteccion anti-robots, nos bloquea. Hay que pedirle a quien administra tu pagina que nos permita el paso.\n  - Si tu pagina se arma con JavaScript, llega vacia. Apunta a una pagina de contenido, no a la portada animada.\n  - NO apuntes a tu landing de LEADCEM: tres clientes lo hicieron y la IA no leyo nada, porque esa pagina se arma en el navegador.\n\nRecorre hasta 25 paginas del mismo dominio, 2 niveles. La foto queda estatica: si actualizas tu sitio, hay que volver a validar.\n\nComo saber si funciono: al validar te dice cuantas paginas y cuantos caracteres leyo. Si dice 0, NO esta funcionando y te explica por que."} /></Label>
                <Input
                  value={agentConfig?.knowledge_source_url || ''}
                  onChange={e => setAgentConfig({ ...agentConfig, knowledge_source_url: e.target.value })}
                  placeholder="https://tu-sitio.com"
                  className="bg-black/20 border-white/10 focus:border-primary"
                  data-testid="ai-agent-knowledge-url"
                />
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={validatingSite || !((agentConfig?.knowledge_source_url || '').trim())}
                    onClick={async () => {
                      const url = (agentConfig?.knowledge_source_url || '').trim();
                      if (!url) { toast.error('Configura primero la URL del sitio'); return; }
                      setValidatingSite(true);
                      try {
                        const r = await api.post('/ai-agent/knowledge-site/validate', { knowledge_source_url: url });
                        const pages = r.data?.pages_crawled || 0;
                        const chars = r.data?.chars_extracted || 0;
                        toast.success(`Sitio validado: ${pages} páginas, ${chars.toLocaleString('es-MX')} caracteres`);
                        setAgentConfig(c => ({
                          ...c,
                          knowledge_source_url: url,
                          knowledge_last_synced_at: r.data?.validated_at,
                          knowledge_site_pages_count: pages,
                          knowledge_sync_error: '',
                        }));
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'No se pudo validar el sitio');
                      } finally {
                        setValidatingSite(false);
                      }
                    }}
                    className="text-xs"
                    data-testid="ai-agent-validate-site-btn"
                  >
                    {validatingSite ? 'Validando…' : 'Validar sitio (radiografía completa)'}
                  </Button>
                  {validatingSite && <span className="text-[10px] text-amber-300">Crawling máx 25 páginas…</span>}
                </div>
                <p className="text-[10px] text-muted-foreground">Al presionar "Validar", {APP_NAME} hace una radiografía same-domain (máx 25 páginas, profundidad 2). El snapshot queda estático hasta la siguiente validación.</p>
                {agentConfig?.knowledge_last_synced_at && (
                  <p className="text-[10px] text-emerald-400" data-testid="ai-agent-last-sync">
                    Última validación: {new Date(agentConfig.knowledge_last_synced_at).toLocaleString('es-MX')}
                    {agentConfig?.knowledge_site_pages_count ? ` · ${agentConfig.knowledge_site_pages_count} páginas` : ''}
                  </p>
                )}
                {agentConfig?.knowledge_sync_error && (
                  <p className="text-[10px] text-amber-400">No se pudo sincronizar el sitio. La IA NO esta usando esta fuente.{String(agentConfig.knowledge_source_url || '').includes('@') ? ' Parece que escribiste un correo en vez de una direccion web.' : String(agentConfig.knowledge_source_url || '').startsWith('http') ? '' : ' Falta https:// al principio de la direccion.'}</p>
                )}
              </div>

              {/* WS-IA-UNIFIED F3 — Habilidades IA del Agente Principal */}
              <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.06] p-4 space-y-3" data-testid="agent-skills-section"><HelpTip title="Habilidades del agente" content={"Son ACCIONES que la IA ejecuta, no temas de los que habla.\n\nCOMO ELIGE QUE MANDAR (la duda mas comun): el sistema le pasa a la IA el catalogo de tus modelos con su identificador, y le ofrece la accion SOLO si el archivo existe. Si un modelo no tiene PDF cargado, la IA ni se entera de que podria mandarlo: no lo inventa ni manda el de otro.\n\nCon 20 modelos, la IA manda el del modelo del que se esta hablando en la conversacion, porque lo identifica por su clave en ese catalogo.\n\nDONDE SE CARGAN LOS ARCHIVOS: en la ficha de cada modelo o propiedad, no aqui. Automotriz: Car Builder, en cada modelo, campos de ficha PDF, video de YouTube y galeria. Inmobiliaria: la ficha se GENERA sola con las fotos de la propiedad, no se sube.\n\nLa lista cambia por vertical: automotriz ve video, agendar cita y cambiar de inventario; inmobiliaria ve brochure y proponer cita; desarrollos ve plano de unidad.\n\nSi no marcas ninguna se usan TODAS las de tu vertical. Marcar sirve para RESTRINGIR. Solo 2 de 25 empresas las han tocado."} />
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <BrainCircuit className="w-4 h-4 text-sky-300" />
                    <p className="text-xs font-bold text-white">Habilidades IA del Agente</p>
                  </div>
                  <Badge className="bg-sky-500/15 text-sky-200 border-sky-400/30 text-[10px]" data-testid="agent-skills-count-badge">
                    {((agentConfig?.active_skills) || []).length === 0
                      ? `Todas (${skillsCatalog.length})`
                      : `${(agentConfig.active_skills || []).length} / ${skillsCatalog.length} Activas`}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Selecciona las acciones que el agente podrá usar (enviar ficha, agendar cita con el vendedor asignado por ruleta, iniciar llamada, etc.). Si no seleccionas ninguna, el agente usa <span className="text-sky-300">todas</span> las habilidades de tu vertical (comportamiento por defecto). Estas habilidades también pueden heredarse en las Campañas IA.
                </p>
                {skillsCatalog.length === 0 ? (
                  <p className="text-[11px] text-muted-foreground" data-testid="agent-skills-empty">
                    No hay habilidades disponibles para este vertical todavía.
                  </p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2.5" data-testid="agent-skills-grid">
                    {skillsCatalog.map((skill) => {
                      const Icon = SKILL_ICONS[skill.key] || Zap;
                      const checked = ((agentConfig?.active_skills) || []).includes(skill.key);
                      return (
                        <label
                          key={skill.key}
                          className={`relative flex cursor-pointer gap-2.5 p-2.5 rounded-xl border bg-black/30 transition-all duration-200 ease-in-out hover:border-sky-500/40 ${checked ? 'border-sky-500/50 bg-sky-500/10' : 'border-white/10'}`}
                          data-testid={`agent-skill-card-${skill.key}`}
                        >
                          <Icon className={`w-4 h-4 shrink-0 mt-0.5 transition-colors ${checked ? 'text-sky-300' : 'text-white/50'}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-semibold text-white truncate">{skill.label}</p>
                            <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{skill.description}</p>
                            {skill.hook ? (
                              <p className="text-[10px] text-sky-200/80 leading-snug mt-1">Gatillo: {skill.hook}</p>
                            ) : null}
                          </div>
                          <Switch
                            checked={checked}
                            onCheckedChange={() => toggleAgentSkill(skill.key)}
                            aria-label={`Activar habilidad ${skill.label}`}
                            data-testid={`agent-skill-checkbox-${skill.key}`}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Los cambios se guardan con el botón Guardar Configuracion del Agente al final de esta tarjeta.
                </p>
                <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-1" data-testid="agent-campaigns-submodulo">
                  <p className="text-xs font-semibold text-white">Campañas IA = este mismo asistente</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    No es otro cerebro. Pestaña «Campañas de este asistente»: mismo prompt, mismas habilidades de tu vertical.
                  </p>
                  <button type="button" className="text-[11px] text-sky-300 hover:underline" onClick={() => setIaSub('campanas')}>Abrir campañas de este asistente →</button>
                </div>
              </div>

              {/* Devolución automática a IA tras inactividad humana */}
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3" data-testid="ai-agent-auto-handback-section"><HelpTip title="Devolucion automatica" content={"Cuando un vendedor toma el control de una conversacion y luego la deja olvidada, esto se la devuelve a la IA para que el cliente no quede sin respuesta.\n\nLo usan 7 de 25 empresas, con esperas de 10 a 120 minutos. Funciona: hay 26 conversaciones detenidas en la plataforma y ninguna es de esas 7 empresas.\n\nSi tu equipo se queja de que la IA les 'quita' conversaciones, sube los minutos en vez de apagarlo."} />
                <div>
                  <p className="text-xs font-bold text-white">Devolución automática a IA</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Si un agente humano toma control y no responde en el tiempo configurado, la conversación regresa automáticamente al agente de IA.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={agentConfig?.auto_handback_enabled || false}
                    onCheckedChange={v => setAgentConfig({ ...agentConfig, auto_handback_enabled: v })}
                    data-testid="auto-handback-toggle"
                  />
                  <Label className="text-xs text-white">Activar devolución automática</Label>
                </div>
                {agentConfig?.auto_handback_enabled && (
                  <div>
                    <Label className="text-[11px] text-white">Minutos sin respuesta del agente humano</Label>
                    <Input
                      type="number"
                      min={5}
                      max={1440}
                      value={Number.isFinite(Number(agentConfig?.auto_handback_minutes)) ? agentConfig.auto_handback_minutes : 30}
                      onChange={e => {
                        const n = parseInt(e.target.value, 10);
                        setAgentConfig({ ...agentConfig, auto_handback_minutes: Number.isFinite(n) ? n : 30 });
                      }}
                      className="bg-black/30 border-white/10 text-xs mt-1 max-w-[160px]"
                      data-testid="auto-handback-minutes-input"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">Mínimo 5, máximo 1440 (24 horas)</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => saveAgentConfig({ ...agentConfig, system_prompt_override: '' })}
                  disabled={agentSaving || agentAnalyzing}
                  className="border-white/10 text-white"
                  data-testid="reset-agent-prompt-btn"
                >
                  Restablecer Prompt
                </Button>
                <Button
                  onClick={() => saveAgentConfig(agentConfig)}
                  disabled={agentSaving || agentAnalyzing}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="save-agent-config-btn"
                >
                  {agentAnalyzing ? 'Analizando configuración...' : agentSaving ? 'Guardando...' : 'Guardar Configuracion del Agente'}
                </Button>
              </div>
              <Button
                onClick={() => saveAgentConfig(agentConfig)}
                disabled={agentSaving}
                className="hidden"
                data-testid="save-agent-config-btn-legacy"
              >
                {agentSaving ? 'Guardando...' : 'Guardar Configuracion del Agente'}
              </Button>
              <div className="p-3 rounded-lg bg-black/20 border border-white/5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-bold">Como funciona</p>
                <ul className="text-[11px] text-muted-foreground space-y-1">
                  <li>1. Activa el agente y conecta WhatsApp</li>
                  <li>2. Cuando un cliente envia un mensaje, el agente IA responde automaticamente</li>
                  <li>3. Usa tecnicas SPIN, Challenger y Sandler para calificar al lead</li>
                  <li>4. Cuando detecta interes serio, te notifica por WhatsApp</li>
                  <li>5. Toma el control desde la pagina de Agente IA en el menu</li>
                </ul>
              </div>
            </CardContent>
          </Card>
          {/* IA la paga LeadCEM — ya no se pegan llaves de tenant */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="ai-api-keys-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Key className="w-4 h-4 text-amber-400" />IA de la plataforma</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">LeadCEM paga OpenRouter. No pegues llaves aquí: si OpenRouter se agota, el respaldo es el panel Tokens de IA (Super Admin).</CardDescription>
            </CardHeader>
          </Card>
          </div>
        </TabsContent>
        <TabsContent value="whatsapp" className="space-y-6">
          <WhatsAppPage />
        </TabsContent>
        <TabsContent value="integraciones" className="space-y-6">
          {/* ===== GRUPO: ENTRA ===== */}
          <div className="pt-2 pb-1 border-b border-white/5">
            <p className="text-sm font-semibold text-white flex items-center gap-2"><span>📥</span> Lo que ENTRA a tu negocio</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Cómo te llegan y te contactan tus clientes: llamadas que entran, formularios de campañas y citas.</p>
          </div>
          {/* G2: Recepción telefónica (interruptor + número + webhook) */}
          <RecepcionTelefonicaCard
            callMode={agentConfig?.call_mode}
            onEditCallMode={() => { setActiveMainTab('ia'); setTimeout(() => { try { document.querySelector('[data-testid="ai-agent-callmode-wrap"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {} }, 250); }}
          />
          <VozAsistenteCard />
          {/* Webhook */}
          <Card className="bg-card/50 backdrop-blur-md border-white/5">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Globe className="w-4 h-4 text-violet-400" />Entrada de leads por integración externa</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">URL genérica para conectar herramientas externas (Zapier, un formulario propio). Cada campaña genera su PROPIA URL con seguimiento — usa esta solo para leads sin campaña.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-3 rounded-lg bg-black/30 border border-white/5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">POST Endpoint</p>
                <code className="text-xs text-primary font-mono break-all" data-testid="webhook-url">
                  {window.location.origin}/api/webhook/lead/{settings?.id}
                </code>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Envía un JSON con: name, email, phone, source. Ojo: los leads que entran por aquí NO se atribuyen a ninguna campaña.</p>
            </CardContent>
          </Card>

          {/* Calendly movido a la Agenda */}
          <Card className="border-blue-500/20 bg-blue-500/5" data-testid="calendly-moved-banner">
            <CardContent className="p-4 text-xs text-blue-200 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-400 shrink-0" />
              <span>Calendly ahora se conecta con un clic desde <a href="/agenda" className="underline text-blue-300">Agenda</a> (junto a Google Calendar y Outlook).</span>
            </CardContent>
          </Card>
          {/* ===== GRUPO: SALE ===== */}
          <div className="pt-2 pb-1 border-b border-white/5">
            <p className="text-sm font-semibold text-white flex items-center gap-2"><span>📤</span> Lo que SALE de tu negocio</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Cómo tu negocio contacta a tus clientes: la Voz IA que marca, correos y WhatsApp del equipo.</p>
          </div>
          {/* Twilio Voice */}
          <TwilioVoiceConfigCard />
          <CorreoConfig />
          <Card className="border-emerald-500/20 bg-emerald-500/5" data-testid="whatsapp-moved-banner">
            <CardContent className="p-4 text-xs text-emerald-200">
              WhatsApp (cuentas, proveedor, números, pruebas y avisos) ahora vive en la{' '}
              <button type="button" onClick={() => setActiveMainTab('whatsapp')} className="underline text-emerald-300">pestaña WhatsApp</button> de aquí mismo.
            </CardContent>
          </Card>
          {isSuperAdmin && (
            <div className="pt-2 pb-1 border-b border-white/5">
              <p className="text-sm font-semibold text-white flex items-center gap-2"><span>💳</span> Cobros en línea</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Pasarelas de pago. Visible solo para el administrador de la plataforma.</p>
            </div>
          )}
          {isSuperAdmin && <StripeConfigCard />}
          {isSuperAdmin && <MercadoPagoConfigCard />}
        </TabsContent>

        {/* TAB: API removido — ahora vive en /dev-hub (API Hub).
            Banner persistente arriba redirige a usuarios que lleguen aquí buscando keys. */}

        {/* ==================== TAB: COMISIONES ==================== */}
        <TabsContent value="comisiones" className="space-y-6">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="commissions-config-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-400" />Configuracion de Comisiones</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Si el inmueble tiene % Comisión Base, ese porcentaje manda. Estos valores son el respaldo cuando el inmueble no trae %.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {commConfig && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">% Comision por Venta</Label>
                      <Input data-testid="comm-sale-pct" type="number" step="0.1" value={commConfig.default_sale_pct} onChange={e => setCommConfig({...commConfig, default_sale_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">% Comision por Renta (del total mensual)</Label>
                      <Input data-testid="comm-rent-pct" type="number" step="0.1" value={commConfig.default_rent_pct} onChange={e => setCommConfig({...commConfig, default_rent_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Meses de Renta (comision)</Label>
                      <Input data-testid="comm-rent-months" type="number" value={commConfig.rent_months} onChange={e => setCommConfig({...commConfig, rent_months: parseInt(e.target.value) || 1})} className="bg-black/20 border-white/10 text-xs" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">% Bono por Captacion</Label>
                      <Input data-testid="comm-brought-pct" type="number" step="0.1" value={commConfig.brought_by_pct} onChange={e => setCommConfig({...commConfig, brought_by_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
                      <p className="text-[10px] text-muted-foreground">Comision extra por traer la propiedad</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">% Comision por Referido</Label>
                      <Input data-testid="comm-referral-pct" type="number" step="0.1" value={commConfig.referral_pct} onChange={e => setCommConfig({...commConfig, referral_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/10">
                    <p className="text-xs font-medium text-amber-400 mb-3">Split de Comision (debe sumar 100%)</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">% Inmobiliaria</Label>
                        <Input data-testid="comm-company-split" type="number" step="1" value={commConfig.company_split_pct} onChange={e => { const v = parseFloat(e.target.value) || 0; setCommConfig({...commConfig, company_split_pct: v, agent_split_pct: 100 - v}); }} className="bg-black/20 border-white/10 text-xs" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">% Agente</Label>
                        <Input data-testid="comm-agent-split" type="number" step="1" value={commConfig.agent_split_pct} onChange={e => { const v = parseFloat(e.target.value) || 0; setCommConfig({...commConfig, agent_split_pct: v, company_split_pct: 100 - v}); }} className="bg-black/20 border-white/10 text-xs" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">% IVA / Impuesto</Label>
                      <Input data-testid="comm-tax" type="number" step="0.1" value={commConfig.tax_pct} onChange={e => setCommConfig({...commConfig, tax_pct: parseFloat(e.target.value) || 0})} className="bg-black/20 border-white/10 text-xs" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Dias para Pago</Label>
                      <Input data-testid="comm-payment-days" type="number" value={commConfig.payment_terms_days} onChange={e => setCommConfig({...commConfig, payment_terms_days: parseInt(e.target.value) || 30})} className="bg-black/20 border-white/10 text-xs" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                    <div>
                      <p className="text-sm text-white">Calculo automatico</p>
                      <p className="text-[10px] text-muted-foreground">Calcular comisiones automaticamente al cerrar oportunidades</p>
                    </div>
                    <button onClick={() => setCommConfig({...commConfig, auto_calculate: !commConfig.auto_calculate})} className={`w-11 h-6 rounded-full transition-all relative ${commConfig.auto_calculate ? 'bg-amber-500' : 'bg-white/10'}`} data-testid="comm-auto-calc-toggle">
                      <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${commConfig.auto_calculate ? 'left-[22px]' : 'left-0.5'}`} />
                    </button>
                  </div>
                  <Button onClick={saveCommissions} disabled={commSaving} className="w-full bg-amber-600 hover:bg-amber-500 text-white text-xs" data-testid="save-commissions-btn">
                    {commSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar Comisiones
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== TAB: MARCA BLANCA ==================== */}
        <TabsContent value="whitelabel" className="space-y-6">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="whitelabel-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" />Dominio Personalizado (White Label)</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Configura un dominio propio para que tu equipo acceda con tu marca</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {wlConfig?.available === false && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="whitelabel-disabled-note">
                  <p className="text-xs text-amber-300 font-medium">White Label exclusivo para Franquiciatario</p>
                  <p className="text-[10px] text-amber-200/70 mt-1">Tu licencia actual no puede activar dominio personalizado. Esta funcionalidad queda reservada para Franquiciatarios y Super Admin.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs">Dominio Personalizado</Label>
                <Input data-testid="wl-domain" value={wlConfig?.custom_domain || ''} onChange={e => setWlConfig({...wlConfig, custom_domain: e.target.value})} placeholder="crm.tuinmobiliaria.com" className="bg-black/20 border-white/10 text-xs font-mono" disabled={wlConfig?.available === false} />
              </div>

              {/* DNS Provider Selector */}
              <div className="space-y-3">
                <Label className="text-xs">Tu proveedor de DNS</Label>
                <div className="grid grid-cols-5 gap-1.5" data-testid="dns-provider-selector">
                  {[
                    { id: 'general', label: 'General' },
                    { id: 'godaddy', label: 'GoDaddy' },
                    { id: 'namecheap', label: 'Namecheap' },
                    { id: 'cloudflare', label: 'Cloudflare' },
                    { id: 'siteground', label: 'SiteGround' },
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => setDnsProvider(p.id)}
                      className={`px-2 py-2 rounded-lg text-[10px] font-medium border transition-colors ${dnsProvider === p.id ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400' : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/10'}`}
                      data-testid={`dns-${p.id}`}
                    >{p.label}</button>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-cyan-500/5 border border-cyan-500/10 space-y-3">
                <p className="text-xs font-semibold text-cyan-400">Instrucciones para {
                  dnsProvider === 'godaddy' ? 'GoDaddy' : dnsProvider === 'namecheap' ? 'Namecheap' : dnsProvider === 'cloudflare' ? 'Cloudflare' : dnsProvider === 'siteground' ? 'SiteGround' : 'tu proveedor'
                }</p>
                <div className="space-y-2 text-[11px] text-muted-foreground">
                  {dnsProvider === 'godaddy' && (<>
                    <p><strong className="text-white">1.</strong> Entra a <a href="https://dcc.godaddy.com" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">dcc.godaddy.com</a> y selecciona tu dominio.</p>
                    <p><strong className="text-white">2.</strong> Ve a <strong className="text-white">DNS &gt; DNS Records &gt; Add New Record</strong>.</p>
                    <p><strong className="text-white">3.</strong> Tipo: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">CNAME</code>, Host: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">crm</code>, Points to: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">{platformHost || '⚠️ FRONTEND_URL no configurada'}</code>, TTL: 1 hora.</p>
                    <p><strong className="text-white">4.</strong> Guarda y espera hasta 48 horas para la propagacion.</p>
                  </>)}
                  {dnsProvider === 'namecheap' && (<>
                    <p><strong className="text-white">1.</strong> Entra a <a href="https://ap.www.namecheap.com" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">ap.www.namecheap.com</a>, ve a <strong className="text-white">Domain List</strong>.</p>
                    <p><strong className="text-white">2.</strong> Click en <strong className="text-white">Manage</strong> junto a tu dominio, luego <strong className="text-white">Advanced DNS</strong>.</p>
                    <p><strong className="text-white">3.</strong> Click <strong className="text-white">Add New Record</strong>: Type: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">CNAME</code>, Host: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">crm</code>, Target: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">{platformHost || '⚠️ FRONTEND_URL no configurada'}</code>.</p>
                    <p><strong className="text-white">4.</strong> Si tienes un CNAME existente para "crm", eliminalo primero.</p>
                  </>)}
                  {dnsProvider === 'cloudflare' && (<>
                    <p><strong className="text-white">1.</strong> Entra a <a href="https://dash.cloudflare.com" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">dash.cloudflare.com</a>, selecciona tu dominio.</p>
                    <p><strong className="text-white">2.</strong> Ve a <strong className="text-white">DNS &gt; Records &gt; Add Record</strong>.</p>
                    <p><strong className="text-white">3.</strong> Type: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">CNAME</code>, Name: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">crm</code>, Target: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">{platformHost || '⚠️ FRONTEND_URL no configurada'}</code>.</p>
                    <p><strong className="text-white">4.</strong> <strong className="text-amber-400">IMPORTANTE:</strong> Desactiva el proxy naranja (icono de nube) y dejalo en <strong className="text-white">DNS only</strong> (nube gris) para que el SSL funcione.</p>
                  </>)}
                  {dnsProvider === 'siteground' && (<>
                    <p><strong className="text-white">1.</strong> Entra a <a href="https://tools.siteground.com" target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline">tools.siteground.com</a> (Site Tools).</p>
                    <p><strong className="text-white">2.</strong> Ve a <strong className="text-white">Domain &gt; DNS Zone Editor</strong>.</p>
                    <p><strong className="text-white">3.</strong> En la seccion de registros, click <strong className="text-white">CNAME</strong> en las pestanas de tipo.</p>
                    <p><strong className="text-white">4.</strong> Name: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">crm</code>, Resolves to: <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">{platformHost || '⚠️ FRONTEND_URL no configurada'}</code>. Click <strong className="text-white">Create</strong>.</p>
                    <p><strong className="text-white">5.</strong> Si tu dominio usa los nameservers de SiteGround, la propagacion es casi inmediata. Si no, espera hasta 24 horas.</p>
                  </>)}
                  {dnsProvider === 'general' && (<>
                    <p><strong className="text-white">1.</strong> Ingresa al panel de administracion de tu proveedor de dominio.</p>
                    <p><strong className="text-white">2.</strong> Busca la seccion de <strong className="text-white">DNS</strong> o <strong className="text-white">Zone Editor</strong>.</p>
                    <p><strong className="text-white">3.</strong> Crea un registro <code className="bg-black/30 px-1 py-0.5 rounded text-cyan-300">CNAME</code> apuntando tu subdominio a:</p>
                  </>)}

                  <div className="p-2 bg-black/30 rounded-lg border border-white/5 font-mono text-cyan-300 flex items-center justify-between">
                    <span>{platformHost || '⚠️ FRONTEND_URL no configurada'}</span>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => { navigator.clipboard.writeText(platformHost); toast.success('Copiado'); }}>Copiar</Button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px] border-collapse">
                      <thead><tr className="border-b border-white/10">
                        <th className="text-left p-1.5 text-muted-foreground">Tipo</th>
                        <th className="text-left p-1.5 text-muted-foreground">Nombre/Host</th>
                        <th className="text-left p-1.5 text-muted-foreground">Valor/Destino</th>
                        <th className="text-left p-1.5 text-muted-foreground">TTL</th>
                      </tr></thead>
                      <tbody><tr className="border-b border-white/5">
                        <td className="p-1.5 text-cyan-300 font-mono">CNAME</td>
                        <td className="p-1.5 text-white font-mono">crm</td>
                        <td className="p-1.5 text-white font-mono">{platformHost || '⚠️ FRONTEND_URL no configurada'}</td>
                        <td className="p-1.5 text-muted-foreground">Auto / 3600</td>
                      </tr></tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Guarda aqui tu dominio y espera hasta 24 horas para la propagacion DNS. El SSL se configura automaticamente.</p>
                </div>
              </div>

              {wlConfig?.verified && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <p className="text-xs text-emerald-400">Dominio verificado y activo</p>
                </div>
              )}

              <Button onClick={saveWhitelabel} disabled={wlSaving || wlConfig?.available === false} className="w-full bg-cyan-600 hover:bg-cyan-500 text-white text-xs" data-testid="save-whitelabel-btn">
                {wlSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar Dominio
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== TAB: TEMPLATES ==================== */}
        <TabsContent value="templates" className="space-y-6">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="templates-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base text-white flex items-center gap-2"><FileText className="w-4 h-4 text-pink-400" />Templates de Landing Page</CardTitle>
              <CardDescription className="text-muted-foreground text-xs">Selecciona el diseno visual de tu landing page publica</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {landingTemplates.map(tpl => {
                  const isSelected = selectedTemplate === tpl.id;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => selectTemplate(tpl.id)}
                      disabled={templateSaving}
                      className={`text-left rounded-xl overflow-hidden border-2 transition-all ${isSelected ? 'border-pink-500 ring-2 ring-pink-500/20' : 'border-white/5 hover:border-white/15'}`}
                      data-testid={`template-${tpl.id}`}
                    >
                      {/* Template Preview */}
                      <div className="h-36 relative" style={{
                        background: tpl.preview_colors.bg,
                        borderBottom: `3px solid ${tpl.preview_colors.accent === 'primary' ? (settings?.primary_color || '#A5D62C') : (settings?.secondary_color || '#1B5E7B')}`
                      }}>
                        <div className="absolute inset-0 p-3 flex flex-col justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded" style={{ background: tpl.preview_colors.accent === 'primary' ? (settings?.primary_color || '#A5D62C') : (settings?.secondary_color || '#1B5E7B') }} />
                            <div className="w-16 h-1.5 rounded" style={{ background: tpl.preview_colors.text, opacity: 0.3 }} />
                          </div>
                          <div className="space-y-1">
                            <div className="w-3/4 h-2 rounded" style={{ background: tpl.preview_colors.text, opacity: 0.6 }} />
                            <div className="w-1/2 h-1.5 rounded" style={{ background: tpl.preview_colors.text, opacity: 0.2 }} />
                          </div>
                          <div className="flex gap-1.5">
                            {[1,2,3].map(i => (
                              <div key={i} className="flex-1 h-10 rounded" style={{ background: tpl.preview_colors.text, opacity: 0.08 }} />
                            ))}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      {/* Template Info */}
                      <div className="p-3 bg-card/80">
                        <p className="text-sm font-semibold text-white">{tpl.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{tpl.description}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tpl.features.map((f, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded text-[8px] bg-white/5 text-muted-foreground">{f}</span>
                          ))}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== TAB: CAMPANAS ==================== */}
        <TabsContent value="campanas" className="space-y-6">
          <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="campaigns-card">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-base text-white flex items-center gap-2"><Zap className="w-4 h-4 text-violet-400" />Campanas de Marketing</CardTitle>
                <CardDescription className="text-muted-foreground text-xs mt-1">Configura campanas por Medio y Canal para rastrear leads</CardDescription>
              </div>
              <Button onClick={() => { setCampaignForm({ name: '', medium: 'digital', channel: '', channel_manual: '', call_mode: '', link_type: 'form', whatsapp_message: '', whatsapp_number: '', form_url: '', inventory_intent: '', development_id: '', operation_type: '' }); setCampaignDialog(true); }} variant="outline" size="sm" className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10 text-xs" data-testid="add-campaign-btn"><Plus className="w-3 h-3 mr-1" />Nueva Campana</Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const visibleCamps = campaigns;  // consolidacion: Configuracion muestra TODAS, incl. scoped (0 perdida)
                return visibleCamps.length === 0 ? (
                <div className="text-center py-6">
                  <Zap className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-xs text-muted-foreground">No hay campanas configuradas.</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Crea tu primera campana para rastrear de donde vienen tus leads.</p>
                </div>
              ) : (
                visibleCamps.map(c => (
                  <div key={c.id} className="p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`campaign-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${c.medium === 'digital' ? 'bg-violet-500/10 text-violet-400' : 'bg-amber-500/10 text-amber-400'}`}>
                          {c.medium === 'digital' ? 'D' : 'I'}
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">{c.name}</p>
                          <p className="text-[10px] text-muted-foreground">{c.medium === 'digital' ? 'Digital' : 'Impreso'} / {c.channel} / {c.link_type === 'whatsapp' ? 'WhatsApp' : 'Formulario'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(c.product_id || c.car_model_id || c.development_id) && (
                          <Badge variant="outline" className="text-[9px] border-violet-400/30 text-violet-300 bg-violet-400/5" data-testid={`campaign-scope-badge-${c.id}`}>
                            {c.product_id ? 'Producto' : c.car_model_id ? 'Modelo' : 'Desarrollo'}
                          </Badge>
                        )}
                        {c.group_id ? (
                          <Badge variant="outline" className="text-[9px] border-cyan-400/30 text-cyan-300 bg-cyan-400/5" data-testid={`campaign-group-badge-${c.id}`}>
                            {campaignGroups.find(g => g.id === c.group_id)?.name || 'Grupo'}
                          </Badge>
                        ) : (!c.is_system_generated && (
                          <Badge variant="outline" className="text-[9px] border-amber-400/30 text-amber-300 bg-amber-400/5" data-testid={`campaign-nogroup-badge-${c.id}`}>
                            Sin grupo
                          </Badge>
                        ))}
                        <Badge variant="outline" className={`text-[9px] ${c.is_active === false ? 'border-rose-400/30 text-rose-300 bg-rose-400/5' : 'border-white/10'}`}>{c.is_active === false ? 'Apagada' : 'Activa'}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => { setCampaignForm({...c}); setCampaignDialog(true); }} className="h-6 w-6 p-0 text-muted-foreground hover:text-violet-400"><Pencil className="w-3 h-3" /></Button>
                        {c.is_active !== false && !c.non_deletable && (
                          <Button variant="ghost" size="sm" onClick={() => deleteCampaign(c.id)} title="Apagar campaña (no se puede volver a encender)" className="h-6 w-6 p-0 text-muted-foreground hover:text-rose-400"><Power className="w-3 h-3" /></Button>
                        )}
                      </div>
                    </div>
                    {c.is_active !== false && (
                      <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-2" data-testid={`campaign-budget-row-${c.id}`}>
                        <DollarSign className="w-3 h-3 text-amber-400 shrink-0" />
                        <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                          Presupuesto {currentPeriod}:
                          <HelpTip title="Presupuesto mensual" content="El presupuesto se guarda por mes. Al cambiar de mes, el campo se reinicia -- no se arrastra el monto anterior. Un mes ya cerrado (pasado) no se puede modificar; captura el presupuesto del mes en curso antes de que termine." />
                        </span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={campaignBudgets[c.id] != null ? String(campaignBudgets[c.id]) : 'Sin definir'}
                          value={budgetDrafts[c.id] ?? ''}
                          onChange={e => setBudgetDrafts(d => ({ ...d, [c.id]: e.target.value }))}
                          className="h-6 w-28 bg-black/20 border-white/10 text-[10px]"
                          data-testid={`campaign-budget-input-${c.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!budgetDrafts[c.id] || budgetSaving[c.id]}
                          onClick={() => saveCampaignBudget(c.id)}
                          className="h-6 px-2 text-[9px] border-amber-400/30 text-amber-300 hover:bg-amber-400/10"
                          data-testid={`campaign-budget-save-${c.id}`}
                        >
                          {budgetSaving[c.id] ? 'Guardando…' : 'Guardar'}
                        </Button>
                      </div>
                    )}
                    {c.webhook_url && (
                      <div className="mt-2 pt-2 border-t border-white/5">
                        <p className="text-[10px] text-cyan-300 break-all" data-testid={`campaign-webhook-${c.id}`}>Webhook: {c.webhook_url}</p>
                      </div>
                    )}
                    {Array.isArray(c.setup_guide) && c.setup_guide.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid={`campaign-guide-${c.id}`}>
                        {c.setup_guide.map((step, idx) => (
                          <p key={idx} className="text-[10px] text-muted-foreground"><span className="text-violet-300 mr-1">{idx + 1}.</span>{step}</p>
                        ))}
                      </div>
                    )}
                    {/* QR Code for print campaigns */}
                    {(c.medium === 'print' || c.link_type === 'whatsapp') && (
                      <div className="mt-2 pt-2 border-t border-white/5">
                        {c.qr_code ? (
                          <div className="flex items-center gap-3">
                            <img src={`data:image/png;base64,${c.qr_code}`} alt="QR" className="w-16 h-16 rounded border border-white/10" />
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] text-muted-foreground truncate">URL: {c.qr_url || 'N/A'}</p>
                              <div className="flex gap-1.5 mt-1">
                                <Button variant="ghost" size="sm" className="h-5 text-[9px] text-blue-400 px-1.5" onClick={() => {
                                  const a = document.createElement('a');
                                  a.href = `data:image/png;base64,${c.qr_code}`;
                                  a.download = `QR_${c.name.replace(/\s/g, '_')}.png`;
                                  a.click();
                                }} data-testid={`download-qr-${c.id}`}>
                                  <Download className="w-2.5 h-2.5 mr-0.5" />Descargar
                                </Button>
                                <Button variant="ghost" size="sm" className="h-5 text-[9px] text-muted-foreground px-1.5" onClick={() => { navigator.clipboard.writeText(c.qr_url); toast.success('URL copiada'); }}>
                                  <Copy className="w-2.5 h-2.5 mr-0.5" />Copiar URL
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-amber-400" onClick={async () => {
                            try {
                              const company = settings;
                              const slug = company?.slug || '';
                              let targetUrl = '';
                              if (c.link_type === 'whatsapp' && c.whatsapp_number) {
                                const phone = c.whatsapp_number.replace(/[+\s-]/g, '');
                                targetUrl = c.whatsapp_message
                                  ? `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(c.whatsapp_message)}`
                                  : `https://api.whatsapp.com/send?phone=${phone}`;
                              } else if (slug) {
                                targetUrl = `${window.location.origin}/${slug}?utm_campaign=${encodeURIComponent(c.name)}&utm_medium=print`;
                              }
                              if (!targetUrl) { toast.error('No se pudo determinar la URL destino'); return; }
                              await api.post(`/campaigns/${c.id}/qr`, { url: targetUrl });
                              toast.success('QR generado');
                              loadSettings();
                            } catch { toast.error('Error al generar QR'); }
                          }} data-testid={`gen-qr-${c.id}`}>
                            <QrCode className="w-3 h-3 mr-1" />Generar QR
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              );
              })()}

              {/* Default "free messages" campaign info */}
              <div className="p-3 rounded-lg bg-emerald-500/[0.03] border border-emerald-500/10">
                <p className="text-[10px] text-emerald-400/80">Los mensajes de WhatsApp que no coincidan con ninguna campana se asignan automaticamente a la campana <strong>"Mensajes Libres"</strong> para reportes.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Save button - SOLO en el tab General: save() solo persiste /settings (negocio/branding/landing).
          Los demas tabs (IA, Comisiones, Whitelabel, Templates, Campanas, Marcas) tienen su propio boton;
          mostrarlo ahi confirmaba "Guardado" en falso sin persistir nada (riesgo de perdida de datos). */}
      {activeMainTab === 'general' && (
      <div className="mt-6 sticky bottom-6" data-testid="global-settings-save-wrapper">
        <Button onClick={save} className={`w-full font-semibold shadow-lg ${saved ? 'bg-emerald-500 hover:bg-emerald-500/90 shadow-emerald-500/20' : 'bg-primary hover:bg-primary/90 shadow-primary/20'}`} data-testid="save-settings-btn" disabled={saving}>
          {saved ? <><CheckCircle2 className="w-4 h-4 mr-2" />Guardado</> : <><Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar Cambios'}</>}
        </Button>
      </div>
      )}


      {/* Campaign Dialog */}
      <Dialog open={campaignDialog} onOpenChange={setCampaignDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto bg-[#1F2833] border-violet-500/20" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-violet-400 flex items-center gap-2"><Zap className="w-5 h-5" />{campaignForm.id ? 'Editar' : 'Nueva'} Campana</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Nombre de la Campana *</Label>
              <Input data-testid="campaign-name" value={campaignForm.name} onChange={e => setCampaignForm({...campaignForm, name: e.target.value})} placeholder="Black Friday 2026" className="bg-black/20 border-white/10 text-xs" />
            </div>
            {campaignForm.property_id && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="campaign-system-generated-note">
                <p className="text-xs text-emerald-300 font-medium">Campaña automática de propiedad</p>
                <p className="text-[10px] text-emerald-200/70 mt-1">Esta campaña se generó automáticamente desde una propiedad y mantiene el rastreo del lead con la ficha correcta.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs">Medio</Label>
              <div className="grid grid-cols-3 gap-2">
                {['digital', 'print', 'llamada'].map(m => (
                  <button key={m} type="button" onClick={() => setCampaignForm({...campaignForm, medium: m, channel: '', channel_manual: '', call_mode: '', link_type: m === 'llamada' ? 'call' : campaignForm.link_type})} className={`px-3 py-2.5 rounded-lg text-xs font-medium border transition-all ${campaignForm.medium === m ? 'bg-violet-500/10 border-violet-500/30 text-violet-400' : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/10'}`} data-testid={`campaign-medium-${m}`}>{m === 'digital' ? 'Digital' : m === 'print' ? 'Impreso' : 'Llamada'}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Canal</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {(campaignChannels[campaignForm.medium] || []).map(ch => (
                  <button key={ch.id} type="button" onClick={() => setCampaignForm({...campaignForm, channel: ch.id, channel_manual: ''})} className={`px-2 py-2 rounded-lg text-[10px] font-medium border transition-all ${(campaignForm.channel === ch.id && !campaignForm.channel_manual) ? 'bg-violet-500/10 border-violet-500/30 text-violet-400' : 'bg-black/20 border-white/5 text-muted-foreground hover:border-white/10'}`} data-testid={`campaign-channel-${ch.id}`}>{ch.label}</button>
                ))}
              </div>
              <Input value={campaignForm.channel_manual || ''} onChange={e => setCampaignForm({...campaignForm, channel_manual: e.target.value, channel: ''})} placeholder="…o escribe el canal manualmente" className="bg-black/20 border-white/10 text-[11px] mt-1" data-testid="campaign-channel-manual" />
            </div>
            {campaignForm.webhook_url && (
              <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20" data-testid="campaign-dialog-webhook-guide">
                <p className="text-[10px] text-cyan-300 font-medium mb-1">Webhook listo para conectar</p>
                <p className="text-[10px] text-cyan-200/80 break-all">{campaignForm.webhook_url}</p>
              </div>
            )}
            {campaignForm.medium !== 'llamada' && (
            <div className="space-y-2">
              <Label className="text-xs">Vincular a</Label>
              <div className="grid grid-cols-2 gap-2">
                {[{id:'form', label:'Formulario'}, {id:'whatsapp', label:'WhatsApp'}].map(lt => (
                  <button key={lt.id} type="button" onClick={() => {
                    if (lt.id === 'whatsapp') {
                      setCampaignForm({
                        ...campaignForm,
                        link_type: 'whatsapp',
                        channel: 'whatsapp',
                        notify_lead: false,
                        notify_vendor: true,
                        ivr_enabled: true,
                      });
                    } else {
                      setCampaignForm({
                        ...campaignForm,
                        link_type: lt.id,
                        notify_lead: true,
                        notify_vendor: true,
                        ivr_enabled: true,
                      });
                    }
                  }} className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${campaignForm.link_type === lt.id ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-black/20 border-white/5 text-muted-foreground'}`} data-testid={`campaign-link-${lt.id}`}>{lt.label}</button>
                ))}
              </div>
            </div>
            )}
            {campaignForm.link_type === 'whatsapp' && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs">Numero de WhatsApp</Label>
                  <PhoneInput data-testid="campaign-wa-number" value={campaignForm.whatsapp_number} onChange={v => setCampaignForm({...campaignForm, whatsapp_number: v})} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Mensaje de Bienvenida (pre-llenado)</Label>
                  <textarea data-testid="campaign-wa-message" value={campaignForm.whatsapp_message} onChange={e => setCampaignForm({...campaignForm, whatsapp_message: e.target.value})} rows={3} placeholder="Hola, vi su publicidad en [espectacular/revista] y me interesa..." className="w-full bg-black/20 border border-white/10 text-xs rounded-lg px-3 py-2 text-white focus:border-violet-400 resize-none" />
                  <p className="text-[10px] text-muted-foreground">Este mensaje permite identificar de que campana proviene el lead.</p>
                  <button type="button" onClick={() => setCampaignForm({ ...campaignForm, whatsapp_message: 'Hola 👋 Vi su anuncio y quiero que me contacten. Si prefieren, toco el boton 📞 de llamada de WhatsApp para que hablemos por voz.' })} className="text-[10px] text-violet-400 hover:text-violet-300 underline underline-offset-2" data-testid="campaign-wa-preset-llamanos">Usar texto “Llamanos por WhatsApp”</button>
                </div>
                <div className="space-y-2" data-testid="campaign-aic-section">
                  <Label className="text-xs">Agente IA Independiente (opcional)</Label>
                  <Select
                    value={campaignForm.ai_independent_campaign_id || '__none__'}
                    onValueChange={v => setCampaignForm({ ...campaignForm, ai_independent_campaign_id: v === '__none__' ? null : v })}
                  >
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="campaign-aic-select">
                      <SelectValue placeholder="Usar agente global (por defecto)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" className="text-xs">Usar agente global (por defecto)</SelectItem>
                      {aiIndependentCampaigns.map(a => (
                        <SelectItem key={a.id} value={a.id} className="text-xs">{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Si se selecciona, los leads de esta campaña responderán con ese agente.</p>
                </div>
              </>
            )}
            {/* Selector de grupo de asignación — disponible para TODOS los link_types (E-R4).
                Mensajes Libres (is_default_free_messages=true) también lo expone para que
                el motor v2 le aplique el bonus +1000 cuando hay grupo asignado. */}
            {(!campaignForm.is_system_generated || campaignForm.is_default_free_messages) && (
              <div className="space-y-2" data-testid="campaign-group-section">
                <Label className="text-xs">Grupo de asignación</Label>
                <p className="text-[10px] text-muted-foreground">El lead se asignará a un vendedor de este grupo</p>
                <Select
                  value={campaignForm.group_id || '__none__'}
                  onValueChange={v => setCampaignForm({ ...campaignForm, group_id: v === '__none__' ? null : v })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="campaign-group-select">
                    <SelectValue placeholder="Sin grupo (asignación general)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-xs">Sin grupo (asignación general)</SelectItem>
                    {campaignGroups.map(g => (
                      <SelectItem key={g.id} value={g.id} className="text-xs">{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isDeveloper && (campaignProducts.length > 0 || campaignCarModels.length > 0) && (
              <div className="space-y-2" data-testid="campaign-scope-section">
                <Label className="text-xs">Alcance de la campana</Label>
                <p className="text-[10px] text-muted-foreground">
                  {campaignForm.id ? 'El alcance no se puede cambiar tras crear la campana.' : 'Dejala en “Toda la empresa”, o amarrala a un producto/modelo para atribuir sus leads ahi.'}
                </p>
                <Select
                  value={campaignForm.product_id ? `prod:${campaignForm.product_id}` : campaignForm.car_model_id ? `car:${campaignForm.car_model_id}` : '__all__'}
                  onValueChange={v => {
                    if (v === '__all__') setCampaignForm({ ...campaignForm, product_id: '', car_model_id: '' });
                    else if (v.startsWith('prod:')) setCampaignForm({ ...campaignForm, product_id: v.slice(5), car_model_id: '' });
                    else if (v.startsWith('car:')) setCampaignForm({ ...campaignForm, car_model_id: v.slice(4), product_id: '' });
                  }}
                  disabled={!!campaignForm.id}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="campaign-scope-select">
                    <SelectValue placeholder="Toda la empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__" className="text-xs">Toda la empresa</SelectItem>
                    {campaignProducts.map(p => (
                      <SelectItem key={`prod:${p.id}`} value={`prod:${p.id}`} className="text-xs">{p.name || p.title || p.id}</SelectItem>
                    ))}
                    {campaignCarModels.map(m => (
                      <SelectItem key={`car:${m.id}`} value={`car:${m.id}`} className="text-xs">{m.name || m.model || m.title || m.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isDeveloper && (
              <div className="space-y-2" data-testid="campaign-development-section">
                <Label className="text-xs">Desarrollo <span className="text-rose-400">*</span></Label>
                <p className="text-[10px] text-muted-foreground">
                  {campaignForm.id ? 'El desarrollo no se puede cambiar tras crear la campaña.' : 'Los leads de esta campaña se atribuyen a este desarrollo.'}
                </p>
                <Select
                  value={campaignForm.development_id || ''}
                  onValueChange={v => setCampaignForm({ ...campaignForm, development_id: v })}
                  disabled={!!campaignForm.id}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="campaign-development-select">
                    <SelectValue placeholder="Selecciona un desarrollo" />
                  </SelectTrigger>
                  <SelectContent>
                    {campaignDevelopments.map(d => (
                      <SelectItem key={d.id} value={d.id} className="text-xs">{d.name || d.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!campaignForm.id && !campaignForm.development_id && (
                  <p className="text-[10px] text-rose-400" data-testid="campaign-development-required">Selecciona un desarrollo</p>
                )}
              </div>
            )}
            {/* tipo-operacion-renta-venta: opcional, solo desarrollo/inmobiliaria (gate por vertical). */}
            {showOperationType && (
              <div className="space-y-2">
                <Label className="text-xs">Tipo de Operacion</Label>
                <Select
                  value={campaignForm.operation_type || 'none'}
                  onValueChange={v => setCampaignForm({ ...campaignForm, operation_type: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="campaign-operation-type-select">
                    <SelectValue placeholder="Sin especificar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-xs">Sin especificar</SelectItem>
                    <SelectItem value="renta" className="text-xs">Renta</SelectItem>
                    <SelectItem value="venta" className="text-xs">Venta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {isCarDealer && campaignForm.channel === 'whatsapp' && (
              <div className="space-y-1" data-testid="campaign-inventory-intent-section">
                <Label className="text-xs">Inventario de interés</Label>
                <p className="text-[10px] text-muted-foreground">
                  Define qué inventario prioriza la IA para los leads de esta campaña
                </p>
                <select
                  className="w-full border border-white/10 rounded px-3 py-2 text-xs bg-black/20 text-white"
                  value={campaignForm.inventory_intent || ''}
                  onChange={(e) =>
                    setCampaignForm(prev => ({
                      ...prev,
                      inventory_intent: e.target.value
                    }))
                  }
                  data-testid="campaign-inventory-intent-select"
                >
                  <option value=''>Sin preferencia (todos los inventarios)</option>
                  <option value='new'>Autos nuevos</option>
                  <option value='used'>Seminuevos</option>
                  <option value='parts'>Refacciones</option>
                  <option value='service'>Servicios</option>
                </select>
              </div>
            )}
            {(campaignForm.link_type === 'form' || campaignForm.link_type === 'whatsapp') && (
              <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-1" data-testid="campaign-notifications-card">
                <p className="text-xs font-semibold text-white mb-1">Notificaciones y llamadas</p>
                <div className="flex items-center justify-between py-2" data-testid="campaign-notify-lead-row">
                  <div>
                    <p className="text-xs font-medium text-white">WhatsApp al lead</p>
                    <p className="text-[10px] text-muted-foreground">Enviar WhatsApp automático al lead cuando se registra</p>
                  </div>
                  <Switch
                    checked={campaignForm.notify_lead !== false}
                    onCheckedChange={v => setCampaignForm({ ...campaignForm, notify_lead: v })}
                    data-testid="campaign-notify-lead-switch"
                  />
                </div>
                <div className="flex items-center justify-between py-2" data-testid="campaign-notify-vendor-row">
                  <div>
                    <p className="text-xs font-medium text-white">WhatsApp al vendedor</p>
                    <p className="text-[10px] text-muted-foreground">Enviar WhatsApp al vendedor asignado cuando llega un lead</p>
                  </div>
                  <Switch
                    checked={campaignForm.notify_vendor !== false}
                    onCheckedChange={v => setCampaignForm({ ...campaignForm, notify_vendor: v })}
                    data-testid="campaign-notify-vendor-switch"
                  />
                </div>
                <div className="flex items-center justify-between py-2" data-testid="campaign-ivr-enabled-row">
                  <div>
                    <p className="text-xs font-medium text-white">Llamada automática</p>
                    <p className="text-[10px] text-muted-foreground">Llamar automáticamente al lead y conectarlo con su asesor</p>
                  </div>
                  <Switch
                    checked={campaignForm.ivr_enabled !== false}
                    onCheckedChange={v => setCampaignForm({ ...campaignForm, ivr_enabled: v })}
                    data-testid="campaign-ivr-enabled-switch"
                  />
                </div>
              </div>
            )}
            <Button onClick={saveCampaign} disabled={isDeveloper && !campaignForm.id && !campaignForm.development_id} className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold text-xs disabled:opacity-50" data-testid="save-campaign-btn">
              <Save className="w-3 h-3 mr-1" /> {campaignForm.id ? 'Actualizar' : 'Crear'} Campana
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal car-dealer: detección de inventario en prompt/FAQs */}
      <Dialog
        open={agentAnalyzeDialog}
        onOpenChange={(open) => {
          if (!agentSaving) {
            setAgentAnalyzeDialog(open);
            if (!open) { setAgentAnalyzeResult(null); setAgentAnalyzePending(null); }
          }
        }}
      >
        <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-md" data-testid="agent-analyze-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">⚠️ Detectamos inventario en tu configuración</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            {agentAnalyzeResult?.detected_items?.length > 0 && (
              <p className="text-amber-300">
                Encontramos estos modelos hardcodeados:{' '}
                <span className="font-mono text-amber-200" data-testid="detected-items">
                  {(agentAnalyzeResult.detected_items || []).join(', ')}
                </span>
              </p>
            )}
            <p className="text-muted-foreground">
              El DMS ya provee el inventario real automáticamente. Te sugerimos usar la versión corregida para evitar conflictos.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                disabled={agentSaving}
                onClick={async () => {
                  if (!agentAnalyzePending || !agentAnalyzeResult) return;
                  const cleaned = {
                    ...agentAnalyzePending,
                    system_prompt_override: agentAnalyzeResult.cleaned_prompt ?? agentAnalyzePending.system_prompt_override,
                    knowledge_faqs: agentAnalyzeResult.cleaned_faqs ?? agentAnalyzePending.knowledge_faqs,
                  };
                  setAgentConfig(cleaned);
                  setAgentAnalyzeDialog(false);
                  setAgentAnalyzeResult(null);
                  setAgentAnalyzePending(null);
                  await _persistAgentConfig(cleaned);
                }}
                data-testid="agent-analyze-use-cleaned"
              >
                Usar versión corregida
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-white/10 text-white text-xs"
                disabled={agentSaving}
                onClick={() => {
                  setAgentAnalyzeDialog(false);
                  setAgentAnalyzeResult(null);
                  setAgentAnalyzePending(null);
                }}
                data-testid="agent-analyze-edit-manual"
              >
                Editar manualmente
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-muted-foreground"
                disabled={agentSaving}
                onClick={async () => {
                  if (!agentAnalyzePending) return;
                  const original = agentAnalyzePending;
                  setAgentAnalyzeDialog(false);
                  setAgentAnalyzeResult(null);
                  setAgentAnalyzePending(null);
                  await _persistAgentConfig(original);
                }}
                data-testid="agent-analyze-save-anyway"
              >
                Guardar de todas formas
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function StripeConfigCard() {
  const PLANS = [
    { id: 'basic', name: 'Basico', usd: 49, mxn: 899 },
    { id: 'pro', name: 'Profesional', usd: 99, mxn: 1799 },
    { id: 'enterprise', name: 'Empresa', usd: 199, mxn: 3599 },
  ];
  const [loading, setLoading] = useState(false);

  const startCheckout = async (planId) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const r = await api.post('/payments/stripe/checkout', { plan_id: planId, origin_url: origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear sesion de pago'); }
    finally { setLoading(false); }
  };

  // Check for returning from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    const payment = params.get('payment');
    if (payment === 'success' && sid) {
      const poll = async (attempts = 0) => {
        try {
          const r = await api.get(`/payments/stripe/status/${sid}`);
          if (r.data.payment_status === 'paid') {
            toast.success(`Plan ${r.data.plan_id} activado exitosamente!`);
            window.history.replaceState({}, '', '/settings');
            return;
          }
          if (attempts < 5) setTimeout(() => poll(attempts + 1), 2000);
        } catch {}
      };
      poll();
    }
  }, []);

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="stripe-config-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-indigo-400" />Stripe - Pagos con Tarjeta
          <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 text-[10px] ml-2">USD</Badge>
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Paga tu suscripcion con tarjeta de credito/debito via Stripe
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PLANS.map(p => (
            <div key={p.id} className="p-3 rounded-lg bg-black/20 border border-white/5 flex flex-col items-center gap-2">
              <p className="text-xs font-medium text-white">{p.name}</p>
              <p className="text-lg font-bold text-indigo-400">${p.usd} USD<span className="text-[10px] text-muted-foreground">/mes</span></p>
              <Button size="sm" onClick={() => startCheckout(p.id)} disabled={loading} className="bg-indigo-600 hover:bg-indigo-500 text-[10px] w-full" data-testid={`stripe-plan-${p.id}`}>
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Suscribirse'}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function MercadoPagoConfigCard() {
  const [token, setToken] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const PLANS = [
    { id: 'basic', name: 'Basico', mxn: 899 },
    { id: 'pro', name: 'Profesional', mxn: 1799 },
    { id: 'enterprise', name: 'Empresa', mxn: 3599 },
  ];

  useEffect(() => {
    api.get('/payments/mercadopago/config').then(r => setStatus(r.data)).catch(() => {});
  }, []);

  // Poll on return from MP
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('mp_ref');
    const payment = params.get('payment');
    if (payment === 'success' && ref) {
      const poll = async (attempts = 0) => {
        try {
          const r = await api.get(`/payments/mercadopago/status/${ref}`);
          if (r.data.payment_status === 'paid') {
            toast.success(`Plan ${r.data.plan_id} activado!`);
            window.history.replaceState({}, '', '/settings');
            return;
          }
          if (attempts < 5) setTimeout(() => poll(attempts + 1), 2000);
        } catch {}
      };
      poll();
    }
  }, []);

  const save = async () => {
    if (!token.trim()) { toast.error('Access Token requerido'); return; }
    setSaving(true);
    try {
      await api.post('/payments/mercadopago/config', { access_token: token, public_key: publicKey });
      toast.success('Mercado Pago conectado');
      setToken('');
      const s = await api.get('/payments/mercadopago/config');
      setStatus(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const startMPCheckout = async (planId) => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const r = await api.post('/payments/mercadopago/checkout', { plan_id: planId, origin_url: origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setCheckoutLoading(false); }
  };

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="mp-config-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-sky-400" />Mercado Pago
          {status?.is_configured && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] ml-2">Conectado</Badge>}
          <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 text-[10px]">MXN</Badge>
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Paga tu suscripcion con Mercado Pago (tarjeta, OXXO, SPEI). Obtiene tu token en{' '}
          <a href="https://www.mercadopago.com/developers" target="_blank" rel="noreferrer" className="text-sky-400 underline">mercadopago.com/developers</a>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status?.is_configured && (
          <div className="p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/10 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-400">Token: {status.token_masked}</span>
          </div>
        )}
        <div>
          <Label className="text-xs text-white/60">{status?.is_configured ? 'Actualizar Access Token' : 'Access Token'}</Label>
          <Input type="password" value={token} onChange={e => setToken(e.target.value)} placeholder="APP_USR-..." className="bg-black/20 border-white/10 text-xs font-mono" data-testid="mp-access-token" />
        </div>
        <div>
          <Label className="text-xs text-white/60">Public Key (opcional)</Label>
          <Input value={publicKey} onChange={e => setPublicKey(e.target.value)} placeholder="APP_USR-..." className="bg-black/20 border-white/10 text-xs font-mono" data-testid="mp-public-key" />
        </div>
        <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-xs" data-testid="mp-save-btn">
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          {status?.is_configured ? 'Actualizar' : 'Conectar'}
        </Button>

        {status?.is_configured && (
          <div className="pt-3 border-t border-white/5">
            <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">Planes (MXN)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {PLANS.map(p => (
                <div key={p.id} className="p-2.5 rounded-lg bg-black/20 border border-white/5 flex flex-col items-center gap-1.5">
                  <p className="text-xs font-medium text-white">{p.name}</p>
                  <p className="text-base font-bold text-sky-400">${p.mxn.toLocaleString()}<span className="text-[10px] text-muted-foreground">/mes</span></p>
                  <Button size="sm" onClick={() => startMPCheckout(p.id)} disabled={checkoutLoading} className="bg-sky-600 hover:bg-sky-500 text-[10px] w-full" data-testid={`mp-plan-${p.id}`}>
                    {checkoutLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Pagar'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecepcionTelefonicaCard({ callMode, onEditCallMode }) {
  const CALL_MODE_LABEL = { enlace_vendedor: 'Enlace al Vendedor', ia: 'Llamada de la IA', fuera_horario: 'Asistencia Fuera de Horario' };
  const CALL_MODE_DESC = { enlace_vendedor: 'La llamada entra y se enlaza directo al vendedor de la ruleta, sin pasar por la IA.', ia: 'La IA atiende, da la información y asigna un vendedor.', fuera_horario: 'La IA atiende cuando no hay nadie en la ruleta y agenda el seguimiento.' };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [phone, setPhone] = useState('');
  const [activa, setActiva] = useState(false);
  const [status, setStatus] = useState(null);
  const [didLocked, setDidLocked] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/voice/config');
      setPhone(r.data?.recepcion_phone_number || '');
      setActiva(Boolean(r.data?.recepcion_activa));
      setStatus(r.data?.recepcion || null);
      setDidLocked((r.data?.recepcion_origen || '') === 'plataforma');
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/voice/config', {
        recepcion_phone_number: phone,
        recepcion_activa: activa,
      });
      setPhone(r.data?.recepcion_phone_number || phone);
      setActiva(Boolean(r.data?.recepcion_activa));
      setStatus(r.data?.recepcion || null);
      toast.success('Recepción guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la recepción');
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = () => {
    const url = status?.webhook_url || '';
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Webhook copiado'),
      () => toast.error('No se pudo copiar'),
    );
  };

  const readyLabel = status?.ready_label || (loading ? '…' : 'sin datos');
  const bannerClass = !status?.plan_ok
    ? 'border-amber-500/30 bg-amber-500/10 text-amber-100'
    : !status?.has_number
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-100'
      : status?.recepcion_activa
        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
        : 'border-sky-500/30 bg-sky-500/10 text-sky-100';

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="recepcion-config-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-emerald-400" />
          Recepción telefónica (llamadas que ENTRAN)
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Cuando un cliente marca el número del anuncio, la llamada se atiende según el modo que elijas
          abajo en 'Cómo se atiende esta llamada'. Apagada por omisión — tú la enciendes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={`p-3 rounded-lg border text-xs ${bannerClass}`} data-testid="recepcion-status-banner">
          <p className="font-medium text-white">Estado: {readyLabel}</p>
          <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
            <li>Tu plan incluye recepción por teléfono: {status?.plan_ok ? 'sí' : 'no'}</li>
            <li>Número exclusivo: {status?.has_number ? 'configurado' : 'falta número'}</li>
            <li>Interruptor: {status?.recepcion_activa ? 'encendido' : 'apagado'}</li>
          </ul>
        </div>

        {/* #5: espejo read-only del modo de atención (se edita en Asistente IA) */}
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-3" data-testid="recepcion-callmode-mirror">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-white font-medium">Cómo se atiende esta llamada</p>
            <button type="button" onClick={onEditCallMode} className="text-[10px] text-amber-300 hover:underline shrink-0" data-testid="recepcion-callmode-edit">Cambiar en Asistente IA →</button>
          </div>
          <p className="text-[11px] text-amber-200 mt-1 font-medium">{CALL_MODE_LABEL[callMode] || 'Sin elegir'}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{CALL_MODE_DESC[callMode] || 'Elige cómo se atiende en la pestaña Asistente IA.'}</p>
        </div>
        <div>
          <Label className="text-xs">Número de recepción (el que marca tu cliente)</Label>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="bg-black/20 border-white/10 text-xs mt-1"
            placeholder="+525597088217"
            data-testid="recepcion-phone"
            disabled={(!status?.plan_ok && status !== null) || didLocked}
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Distinto del número de salida. Debe ser único entre empresas. Formato internacional (+52…).
          </p>
        </div>

        {didLocked && <p className="text-[11px] text-amber-300 flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2" data-testid="recepcion-locked-note">🔒 Este número de recepción lo gestiona LeadCEM. No lo puedes cambiar aquí; contáctanos si necesitas otro.</p>}
        <label
          className="flex items-center justify-between gap-3 rounded-lg border border-white/10 p-3"
          data-testid="recepcion-activa-row"
        >
          <div>
            <p className="text-xs text-white font-medium">Recepción activa</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Al encenderlo, las llamadas al DID se enrutan (ruleta en horario / recado fuera).
              Apagado = el cliente oye un mensaje corto, sin pasar al vendedor.
            </p>
          </div>
          <input
            type="checkbox"
            checked={activa}
            onChange={(e) => setActiva(e.target.checked)}
            className="w-4 h-4"
            data-testid="recepcion-activa-checkbox"
            disabled={!status?.plan_ok && status !== null}
          />
        </label>

        <div className="rounded-lg border border-white/10 bg-black/20 p-3 space-y-2" data-testid="recepcion-webhook-box">
          <p className="text-xs text-white font-medium">Webhook Twilio (copiar y pegar)</p>
          <p className="text-[10px] text-muted-foreground">{status?.twilio_hint}</p>
          <div className="flex gap-2 items-center">
            <code className="flex-1 text-[11px] text-emerald-300 break-all bg-black/40 rounded px-2 py-1.5" data-testid="recepcion-webhook-url">
              {status?.webhook_url || '…'}
            </code>
            <Button type="button" size="sm" variant="outline" className="text-xs border-white/10 shrink-0" onClick={copyWebhook} data-testid="recepcion-webhook-copy">
              Copiar
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">Método: HTTP {status?.webhook_method || 'POST'}</p>
        </div>

        <Button
          onClick={save}
          disabled={saving || loading || (!status?.plan_ok && status !== null)}
          className="bg-emerald-600 hover:bg-emerald-500 text-xs"
          data-testid="recepcion-save-btn"
        >
          {saving ? 'Guardando…' : 'Guardar recepción'}
        </Button>
      </CardContent>
    </Card>
  );
}


function VozAsistenteCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voice, setVoice] = useState('ara');
  const [temp, setTemp] = useState(0.55);
  const voces = [
    { id: 'ara', label: 'Ara · cálida (mujer)' },
    { id: 'eve', label: 'Eve · energética' },
    { id: 'leo', label: 'Leo · firme (hombre)' },
    { id: 'rex', label: 'Rex · clara' },
    { id: 'sal', label: 'Sal · suave' },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/voice/config');
      setVoice(r.data?.ia_voice || 'ara');
      setTemp(Number(r.data?.ia_temperature ?? 0.55));
    } catch { /* sin voz */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/voice/config', { ia_voice: voice, ia_temperature: temp });
      toast.success('Voz del asistente guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="voz-asistente-card">
      <CardHeader>
        <CardTitle className="text-white text-base">Voz del asistente</CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          Cómo suena la IA cuando habla por teléfono. Más "soltura" = más natural; menos = más corta y formal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? <p className="text-xs text-muted-foreground">Cargando…</p> : (
          <>
            <div>
              <Label className="text-xs">Voz</Label>
              <Select value={voice} onValueChange={setVoice}>
                <SelectTrigger className="bg-black/20 border-white/10 text-xs mt-1" data-testid="voz-asistente-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {voces.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Temperatura</Label>
                <span className="text-[10px] font-mono text-muted-foreground">{Number(temp).toFixed(2)}</span>
              </div>
              <Slider value={[temp]} min={0.1} max={1} step={0.05} onValueChange={([v]) => setTemp(v)} className="mt-2" data-testid="voz-asistente-temp" />
            </div>
            <Button size="sm" onClick={save} disabled={saving} className="text-xs">{saving ? 'Guardando…' : 'Guardar voz'}</Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}


function TwilioVoiceConfigCard() {
  const defaultIvrMessages = {
    lead_inicial: 'Hola, gracias por contactarnos. Estamos procesando tu solicitud. Si deseas hablar con un asesor inmediatamente, presiona uno. Si prefieres recibir la información por WhatsApp, simplemente cuelga.',
    lead_conectando: 'Perfecto. Te estamos conectando con un asesor. Por favor espera en línea.',
    lead_asesor_no_disponible: 'Lo sentimos, en este momento no hay asesores disponibles. Te contactaremos a la brevedad por WhatsApp. Gracias por tu paciencia.',
    lead_opcion_invalida: 'No reconocimos tu selección. Te contactaremos pronto. Gracias.',
    vendor_saludo: 'Atención. Tienes un lead en línea esperando. Adelante, puedes hablar ahora.',
  };

  const defaultIvr = {
    enabled: false,
    auto_call_new_leads: false,
    delay_seconds: 5,
    messages: defaultIvrMessages,
  };

  const [status, setStatus] = useState({ configured: false, config_source: 'none', resolved_phone_number: '' });
  const [form, setForm] = useState({ account_sid: '', auth_token: '', phone_number: '' });
  const [vozLocked, setVozLocked] = useState(false);
  const [ivrForm, setIvrForm] = useState(defaultIvr);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/voice/config');
      setStatus(r.data || {});
      setVozLocked((r.data?.voz_origen || '') === 'plataforma');
      setForm((prev) => ({
        account_sid: r.data?.account_sid || prev.account_sid || '',
        auth_token: '',
        phone_number: r.data?.phone_number || prev.phone_number || '',
      }));
      const incomingIvr = r.data?.ivr || {};
      setIvrForm({
        enabled: Boolean(incomingIvr.enabled),
        auto_call_new_leads: Boolean(incomingIvr.auto_call_new_leads),
        delay_seconds: Number.isFinite(Number(incomingIvr.delay_seconds)) ? Number(incomingIvr.delay_seconds) : 5,
        messages: {
          ...defaultIvrMessages,
          ...(incomingIvr.messages || {}),
        },
      });
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post('/voice/config', {
        ...form,
        ivr: ivrForm,
      });
      setStatus(r.data || {});
      toast.success('Twilio Voice configurado');
      setForm((prev) => ({
        account_sid: prev.account_sid,
        auth_token: '',
        phone_number: prev.phone_number,
      }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar Twilio Voice');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('¿Eliminar las credenciales propias de Twilio Voice?')) return;
    try {
      await api.delete('/voice/config');
      toast.success('Credenciales eliminadas');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <Card className="bg-card/50 backdrop-blur-md border-white/5" data-testid="twilio-voice-config-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-base text-white flex items-center gap-2"><PhoneCall className="w-4 h-4 text-emerald-400" />Número propio para la Voz IA (llamadas que SALEN)</CardTitle>
        <CardDescription className="text-muted-foreground text-xs">El número desde el que la IA marca a tus prospectos. Es OPCIONAL: si no pones uno, {APP_NAME} marca por su número compartido. OJO: este NO es el número al que te llaman tus clientes — ese se pone en "Recepción telefónica", arriba.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 rounded-lg bg-black/20 border border-white/5" data-testid="twilio-voice-status">
          <p className="text-xs text-white">Marcando desde: <span className="text-emerald-400">{status.config_source === 'company' ? 'Tu número propio' : (status.config_source || '').includes('platform') ? `Número compartido de ${APP_NAME}` : 'Sin configurar (usa el compartido)'}</span></p>
          <p className="text-[10px] text-muted-foreground mt-1">Número que se usa hoy: {status.resolved_phone_number || 'N/D'}</p>
          <p className="text-[10px] text-muted-foreground mt-1" data-testid="voice-ivr-enabled-status">Menú de opciones por teléfono: {ivrForm.enabled ? 'encendido' : 'apagado'}</p>
        </div>
        {vozLocked && <p className="text-[11px] text-amber-300 flex items-center gap-1 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2" data-testid="voz-locked-note">🔒 Este número y sus credenciales los gestiona LeadCEM. No se pueden cambiar aquí.</p>}
        <div><Label className="text-xs">Account SID</Label><Input value={form.account_sid} onChange={e => setForm({ ...form, account_sid: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="AC..." data-testid="voice-sid" disabled={vozLocked} /></div>
        <div><Label className="text-xs">Auth Token</Label><Input type="password" value={form.auth_token} onChange={e => setForm({ ...form, auth_token: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="Token secreto" data-testid="voice-token" disabled={vozLocked} /></div>
        <div>
          <Label className="text-xs">Tu número para llamadas que salen</Label>
          <Input value={form.phone_number} onChange={e => setForm({ ...form, phone_number: e.target.value })} className="bg-black/20 border-white/10 text-xs mt-1" placeholder="+525597088217" data-testid="voice-phone" disabled={vozLocked} />
          <p className="text-[10px] text-muted-foreground mt-1">Número con el que LeadCEM marca (llamadas salientes / IVR). El DID que el cliente marca en el anuncio se configura arriba en Recepción telefónica.</p>
        </div>

        <div className="border-t border-white/10 pt-4 space-y-3" data-testid="voice-ivr-config-section">
          <h4 className="text-sm text-white font-medium">Opciones avanzadas del teléfono</h4>
          <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 p-2" data-testid="voice-ivr-enabled-row">
            <span className="text-xs text-zinc-200">Habilitar IVR</span>
            <input
              data-testid="voice-ivr-enabled-checkbox"
              type="checkbox"
              checked={ivrForm.enabled}
              onChange={(e) => setIvrForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              className="w-4 h-4"
            />
          </label>

          {ivrForm.enabled ? (
            <>
              <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 p-2" data-testid="voice-ivr-auto-call-row">
                <span className="text-xs text-zinc-200">Llamar automáticamente a leads nuevos</span>
                <input
                  data-testid="voice-ivr-auto-call-checkbox"
                  type="checkbox"
                  checked={ivrForm.auto_call_new_leads}
                  onChange={(e) => setIvrForm((prev) => ({ ...prev, auto_call_new_leads: e.target.checked }))}
                  className="w-4 h-4"
                />
              </label>

              <div data-testid="voice-ivr-delay-row">
                <Label className="text-xs">Delay (segundos)</Label>
                <Input
                  data-testid="voice-ivr-delay-input"
                  type="number"
                  min={0}
                  max={60}
                  value={ivrForm.delay_seconds}
                  onChange={(e) => {
                    const next = Number(e.target.value || 0);
                    setIvrForm((prev) => ({ ...prev, delay_seconds: Number.isFinite(next) ? Math.max(0, Math.min(next, 60)) : 0 }));
                  }}
                  className="bg-black/20 border-white/10 text-xs mt-1 w-28"
                />
              </div>

              <div className="space-y-2" data-testid="voice-ivr-messages-block">
                <Label className="text-xs">Mensaje lead inicial</Label>
                <Textarea
                  data-testid="voice-ivr-msg-lead-inicial"
                  value={ivrForm.messages.lead_inicial}
                  onChange={(e) => setIvrForm((prev) => ({ ...prev, messages: { ...prev.messages, lead_inicial: e.target.value } }))}
                  className="bg-black/20 border-white/10 text-xs"
                  rows={3}
                />

                <Label className="text-xs">Mensaje vendor saludo</Label>
                <Textarea
                  data-testid="voice-ivr-msg-vendor-saludo"
                  value={ivrForm.messages.vendor_saludo}
                  onChange={(e) => setIvrForm((prev) => ({ ...prev, messages: { ...prev.messages, vendor_saludo: e.target.value } }))}
                  className="bg-black/20 border-white/10 text-xs"
                  rows={2}
                />
              </div>
            </>
          ) : null}
        </div>

        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-xs" data-testid="voice-save-btn">{saving ? 'Guardando...' : 'Guardar número de la Voz IA'}</Button>
          {status.config_source === 'company' && <Button variant="outline" onClick={remove} className="text-xs border-white/10">Quitar propias</Button>}
        </div>
      </CardContent>
    </Card>
  );
}


function NotificationPanel() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateGroups, setTemplateGroups] = useState([]);

  const variableSources = [
    { value: 'lead_name', label: 'Nombre del lead' },
    { value: 'lead_property', label: 'Propiedad de interés' },
    { value: 'lead_vendor', label: 'Vendedor asignado' },
  ];

  const extractVariableIndexes = (body) => {
    const text = String(body || '');
    const regex = /\{\{(\d+)\}\}/g;
    const found = new Set();
    let match = regex.exec(text);
    while (match) {
      found.add(Number(match[1]));
      match = regex.exec(text);
    }
    return [...found].sort((a, b) => a - b);
  };

  const defaultSourceForIndex = (index) => {
    if (index === 1) return 'lead_name';
    if (index === 2) return 'lead_property';
    if (index === 3) return 'lead_vendor';
    return 'lead_name';
  };

  const buildDefaultMapping = (indexes) => indexes.map((idx) => ({
    index: idx,
    source: defaultSourceForIndex(idx),
  }));

  const getTemplateFromGroups = (templateId) => {
    for (const group of templateGroups) {
      const found = (group.templates || []).find((tpl) => tpl.id === templateId);
      if (found) return { ...found, group };
    }
    return null;
  };

  const loadGroupedTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const numbersRes = await api.get('/whatsapp/gupshup/numbers');
      const numbers = Array.isArray(numbersRes.data?.numbers) ? numbersRes.data.numbers : [];
      const groups = await Promise.all(numbers.filter((n) => n?.id).map(async (n) => {
        try {
          const tplRes = await api.get(`/whatsapp/gupshup/saved-templates/${n.id}`);
          const templates = Array.isArray(tplRes.data?.templates) ? tplRes.data.templates : [];
          return {
            config_id: n.id,
            source_phone: n.source_phone || 'Sin número',
            app_name: n.app_name || 'Sin app',
            templates,
          };
        } catch {
          return {
            config_id: n.id,
            source_phone: n.source_phone || 'Sin número',
            app_name: n.app_name || 'Sin app',
            templates: [],
          };
        }
      }));
      setTemplateGroups(groups);
    } catch {
      setTemplateGroups([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get('/settings/notifications'),
      loadGroupedTemplates(),
    ]).then(([notifRes]) => {
      const data = notifRes.data || {};
      setConfig({
        new_lead_wa_enabled: Boolean(data.new_lead_wa_enabled),
        new_lead_wa_template: data.new_lead_wa_template || 'Nuevo lead asignado: {lead_name} ({lead_phone}) | Fuente: {lead_source} | Fecha: {fecha}',
        new_lead_wa_to_lead_enabled: Boolean(data.new_lead_wa_to_lead_enabled),
        new_lead_wa_template_id: data.new_lead_wa_template_id || '',
        new_lead_wa_variable_mapping: Array.isArray(data.new_lead_wa_variable_mapping) ? data.new_lead_wa_variable_mapping : [],
      });
    }).catch(() => setConfig({
      new_lead_wa_enabled: false,
      new_lead_wa_template: 'Nuevo lead asignado: {lead_name} ({lead_phone}) | Fuente: {lead_source} | Fecha: {fecha}',
      new_lead_wa_to_lead_enabled: false,
      new_lead_wa_template_id: '',
      new_lead_wa_variable_mapping: [],
    })).finally(() => setLoading(false));
  }, []);

  const selectedTemplate = getTemplateFromGroups(config?.new_lead_wa_template_id);
  const selectedTemplateIndexes = extractVariableIndexes(selectedTemplate?.body || selectedTemplate?.data || '');

  useEffect(() => {
    if (!config?.new_lead_wa_template_id || selectedTemplateIndexes.length === 0) return;
    const currentMap = Array.isArray(config.new_lead_wa_variable_mapping) ? config.new_lead_wa_variable_mapping : [];
    const byIndex = new Map(currentMap.map((m) => [Number(m.index), m]));
    const merged = selectedTemplateIndexes.map((idx) => ({
      index: idx,
      source: byIndex.get(idx)?.source || defaultSourceForIndex(idx),
    }));
    const isDifferent = JSON.stringify(merged) !== JSON.stringify(currentMap);
    if (isDifferent) {
      setConfig((prev) => ({ ...prev, new_lead_wa_variable_mapping: merged }));
    }
  }, [config?.new_lead_wa_template_id, config?.new_lead_wa_variable_mapping, selectedTemplateIndexes.join(',')]);

  const save = async () => {
    if (config.new_lead_wa_to_lead_enabled) {
      if (!config.new_lead_wa_template_id) {
        toast.error('Selecciona una plantilla de Gupshup para la bienvenida al lead');
        return;
      }
      if (!Array.isArray(config.new_lead_wa_variable_mapping) || config.new_lead_wa_variable_mapping.length === 0) {
        toast.error('Define el mapeo de variables de la plantilla');
        return;
      }
    }
    setSaving(true);
    try {
      await api.put('/settings/notifications', config);
      toast.success('Notificaciones actualizadas');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-green-400 animate-spin" /></div>;
  if (!config) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-white">Notificacion de Nuevo Lead por WhatsApp</p>
          <p className="text-[10px] text-muted-foreground">Cuando se asigna un lead nuevo, el vendedor recibe un mensaje de WA</p>
        </div>
        <Switch checked={config.new_lead_wa_enabled} onCheckedChange={v => setConfig({ ...config, new_lead_wa_enabled: v })} data-testid="notif-wa-toggle" />
      </div>

      {config.new_lead_wa_enabled && (
        <div className="space-y-3 p-3 rounded-lg bg-green-500/5 border border-green-500/10">
          <div>
            <Label className="text-[10px] text-white/60">Plantilla del mensaje</Label>
            <Textarea value={config.new_lead_wa_template || ''} onChange={e => setConfig({ ...config, new_lead_wa_template: e.target.value })}
              className="bg-black/20 border-white/10 text-xs min-h-[80px] mt-1" data-testid="notif-wa-template"
              placeholder="Usa: {lead_name}, {lead_phone}, {lead_source}, {vendor_name}, {fecha}" />
          </div>
          <div className="p-2 rounded bg-black/30 border border-white/5">
            <p className="text-[9px] text-muted-foreground mb-1">Variables disponibles:</p>
            <div className="flex flex-wrap gap-1">
              {['{lead_name}', '{lead_phone}', '{lead_source}', '{vendor_name}', '{fecha}'].map(v => (
                <code key={v} className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-[9px]">{v}</code>
              ))}
            </div>
          </div>
          <div className="p-2 rounded bg-green-500/5 border border-green-500/10">
            <p className="text-[9px] text-green-400/70 mb-1">Vista previa:</p>
            <p className="text-[10px] text-white/70">{(config.new_lead_wa_template || '').replace('{lead_name}', 'Maria Lopez').replace('{lead_phone}', '+52 55 1234 5678').replace('{lead_source}', 'Portal Web').replace('{vendor_name}', 'Juan Perez').replace('{fecha}', new Date().toLocaleDateString('es-MX'))}</p>
          </div>
        </div>
      )}

      <div className="space-y-3 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/10" data-testid="notif-lead-welcome-wrapper">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-white" data-testid="notif-lead-welcome-title">WhatsApp de bienvenida al lead</p>
            <p className="text-[10px] text-muted-foreground" data-testid="notif-lead-welcome-subtitle">Envia automaticamente un template de Gupshup cuando se registra un lead</p>
          </div>
          <Switch
            checked={config.new_lead_wa_to_lead_enabled}
            onCheckedChange={v => setConfig({ ...config, new_lead_wa_to_lead_enabled: v })}
            data-testid="notif-lead-welcome-toggle"
          />
        </div>

        {config.new_lead_wa_to_lead_enabled && (
          <div className="space-y-3" data-testid="notif-lead-welcome-config-box">
            <div className="space-y-2">
              <Label className="text-[10px] text-white/70">Plantilla (agrupada por número WABA)</Label>
              {templatesLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="notif-lead-welcome-templates-loading">
                  <Loader2 className="w-3 h-3 animate-spin" />Cargando plantillas...
                </div>
              ) : (
                <select
                  className="w-full bg-black/20 border border-white/10 rounded-md px-2 py-2 text-xs"
                  value={config.new_lead_wa_template_id || ''}
                  onChange={(e) => {
                    const tplId = e.target.value;
                    const tpl = getTemplateFromGroups(tplId);
                    const indexes = extractVariableIndexes(tpl?.body || tpl?.data || '');
                    setConfig({
                      ...config,
                      new_lead_wa_template_id: tplId,
                      new_lead_wa_variable_mapping: buildDefaultMapping(indexes),
                    });
                  }}
                  data-testid="notif-lead-welcome-template-select"
                >
                  <option value="">Selecciona plantilla</option>
                  {templateGroups.map((group) => (
                    <optgroup key={group.config_id} label={`${group.source_phone} — ${group.app_name}`}>
                      {(group.templates || []).map((tpl) => (
                        <option key={tpl.id} value={tpl.id}>{`${tpl.name || 'sin_nombre'} — ${tpl.category || 'N/A'} — ${tpl.language || 'N/A'}`}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>

            {selectedTemplate && selectedTemplateIndexes.length > 0 && (
              <div className="space-y-2 p-2 rounded bg-black/20 border border-white/5" data-testid="notif-lead-welcome-variable-map-box">
                <p className="text-[10px] text-white/70" data-testid="notif-lead-welcome-variable-map-title">Variables detectadas en la plantilla seleccionada</p>
                {selectedTemplateIndexes.map((idx) => {
                  const current = (config.new_lead_wa_variable_mapping || []).find((m) => Number(m.index) === Number(idx));
                  return (
                    <div key={`lead-map-${idx}`} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center" data-testid={`notif-lead-welcome-variable-row-${idx}`}>
                      <Label className="text-[11px] text-cyan-300" data-testid={`notif-lead-welcome-variable-label-${idx}`}>{`{{${idx}}}`}</Label>
                      <select
                        className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs"
                        value={current?.source || defaultSourceForIndex(idx)}
                        onChange={(e) => {
                          const next = [...(config.new_lead_wa_variable_mapping || [])];
                          const pos = next.findIndex((m) => Number(m.index) === Number(idx));
                          const row = { index: idx, source: e.target.value };
                          if (pos >= 0) next[pos] = row;
                          else next.push(row);
                          next.sort((a, b) => Number(a.index) - Number(b.index));
                          setConfig({ ...config, new_lead_wa_variable_mapping: next });
                        }}
                        data-testid={`notif-lead-welcome-variable-source-${idx}`}
                      >
                        {variableSources.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Button onClick={save} disabled={saving} className="bg-green-600 hover:bg-green-500 text-xs" data-testid="notif-save-btn">
        {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
        Guardar Notificaciones
      </Button>
    </div>
  );
}


// ── Componente reutilizable: panel QR de Evolution ────────────────────
// Extraído del bloque inline de WhatsApp tab para que ProveedorTab.js
// pueda renderizarlo sin duplicar lógica. Acepta los setters del padre
// como props opcionales: si se proveen, sincroniza estado con el padre
// (caso SettingsPage); si no, maneja estado interno (caso ProveedorTab).
export function EvolutionQRPanel({
  waQr: extWaQr,
  setWaQr: extSetWaQr,
  waConnecting: extWaConnecting,
  setWaConnecting: extSetWaConnecting,
  waStatus: extWaStatus,
  setWaStatus: extSetWaStatus,
} = {}) {
  const [intWaQr, intSetWaQr] = useState(null);
  const [intWaConnecting, intSetWaConnecting] = useState(false);
  const [intWaStatus, intSetWaStatus] = useState(null);

  // Si el padre no provee setters, usamos los internos.
  const useExternal = typeof extSetWaQr === 'function' && typeof extSetWaStatus === 'function';
  const waQr = useExternal ? extWaQr : intWaQr;
  const setWaQr = useExternal ? extSetWaQr : intSetWaQr;
  const waConnecting = useExternal ? extWaConnecting : intWaConnecting;
  const setWaConnecting = useExternal ? extSetWaConnecting : intSetWaConnecting;
  const waStatus = useExternal ? extWaStatus : intWaStatus;
  const setWaStatus = useExternal ? extSetWaStatus : intSetWaStatus;

  // Modo standalone (ProveedorTab): cargar status al montar.
  useEffect(() => {
    if (useExternal) return;
    api.get('/whatsapp/status').then((r) => intSetWaStatus(r.data)).catch(() => {});
  }, [useExternal]);

  const handleConnect = async () => {
    setWaConnecting(true);
    try {
      const r = await api.post('/whatsapp/connect-qr');
      if (r.data.already_connected) {
        toast.info(r.data.message || 'WhatsApp ya esta conectado');
        const s = await api.get('/whatsapp/status');
        setWaStatus(s.data);
      } else if (r.data.qr_code) {
        setWaQr(r.data.qr_code);
        toast.success('Escanea el codigo QR con tu WhatsApp');
        setTimeout(() => {
          api.get('/whatsapp/status').then((rr) => {
            setWaStatus(rr.data);
            if (rr.data.connected) setWaQr(null);
          });
        }, 15000);
      } else {
        toast.warning(r.data.message || 'No se pudo obtener el QR. Intenta desconectar y reconectar.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al conectar');
    } finally {
      setWaConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/whatsapp/disconnect');
      setWaStatus({ connected: false });
      setWaQr(null);
      toast.success('WhatsApp desconectado');
    } catch {
      toast.error('Error');
    }
  };

  const handleRefresh = async () => {
    try {
      await api.post('/whatsapp/reset-instance');
      toast.success('Instancia reiniciada — escanea el nuevo QR');
      const qr = await api.post('/whatsapp/connect-qr');
      if (qr.data?.qr_code) setWaQr(qr.data.qr_code);
      const s = await api.get('/whatsapp/status');
      setWaStatus(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reiniciar instancia');
    }
  };

  return (
    <div className="space-y-3" data-testid="wa-qr-section">
      {waQr && (
        <div className="p-4 rounded-lg bg-white flex items-center justify-center" data-testid="whatsapp-qr">
          <img src={`data:image/png;base64,${waQr}`} alt="QR WhatsApp" className="w-48 h-48" />
        </div>
      )}
      <div className="flex gap-2">
        {!waStatus?.connected ? (
          <Button
            onClick={handleConnect}
            disabled={waConnecting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="whatsapp-connect-btn"
          >
            {waConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
            {waConnecting ? 'Conectando...' : 'Conectar via QR'}
          </Button>
        ) : (
          <Button
            onClick={handleDisconnect}
            variant="outline"
            className="flex-1 border-destructive/30 text-destructive hover:bg-destructive/10"
            data-testid="whatsapp-disconnect-btn"
          >
            <WifiOff className="w-4 h-4 mr-2" /> Desconectar
          </Button>
        )}
        <Button
          onClick={handleRefresh}
          variant="outline" size="sm" className="border-white/10 text-muted-foreground shrink-0"
          data-testid="whatsapp-refresh-btn"
        >Actualizar</Button>
      </div>
      {waStatus?.error && (
        <p className="text-[11px] text-amber-400/80 p-2 rounded bg-amber-500/5 border border-amber-500/10">
          {waStatus.error.includes('no configurada') ? 'Evolution API no esta instalada. Pide al Super Admin que la configure.' : waStatus.error}
        </p>
      )}
    </div>
  );
}
